// Integración de la gestión de capacidad en la vista de Ingresos

<template>
  <div class="ingresos-container">
    <!-- Tabla de Ingresos -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="ingresos-title">Gestión de Ingresos</span>
        <!-- Validador GS1 (checklist 5.2): verifica simbologías y etiquetas -->
        <v-btn
          color="teal"
          variant="outlined"
          prepend-icon="mdi-check-decagram"
          @click="openValidator"
          size="default"
          class="mr-2"
        >
          <span class="d-none d-sm-inline">Validador GS1</span>
          <span class="d-sm-none">Validar</span>
        </v-btn>
        <v-btn
          color="success"
          variant="tonal"
          prepend-icon="mdi-microsoft-excel"
          @click="exportGs1Excel"
          size="default"
          class="mr-2"
          title="Exportar movimientos de entrada con claves GS1 (GTIN, lote, fechas, serie, SSCC) a Excel"
        >
          <span class="d-none d-sm-inline">Excel GS1</span>
        </v-btn>
        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          @click="showNewIngresoModal"
          size="default"
        >
          <span class="d-none d-sm-inline">Nuevo Ingreso</span>
          <span class="d-sm-none">Nuevo</span>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4 ingresos-filters">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="[
                { title: 'Todos', value: '' },
                { title: 'Completado', value: 'Completado' },
                { title: 'Pendiente', value: 'Pendiente' },
                { title: 'Cancelado', value: 'Cancelado' }
              ]"
              label="Estado"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateTo"
              label="Fecha hasta"
              type="date"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Referencia, proveedor..."
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla -->
        <ResponsiveDataTable
          :items="filteredIngresos"
          :headers="[
            { title: 'Referencia', key: 'reference', align: 'start', sortable: true },
            { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
            { title: 'Proveedor', key: 'supplier', align: 'start', sortable: true },
            { title: 'Ítems', key: 'items_count', align: 'end', sortable: true },
            { title: 'Notas', key: 'notes', align: 'start', sortable: false },
            { title: 'Estado', key: 'status', align: 'center', sortable: true },
            { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
          ]"
          :loading="loading"
          :search="filters.search"
          class="elevation-1 ingresos-table"
          :show-select="false"
          items-per-page="10"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.supplier="{ item }">
            {{ item.supplier || 'No especificado' }}
          </template>

          <template v-slot:item.items_count="{ item }">
            {{ item.details?.length || 0 }}
          </template>

          <template v-slot:item.notes="{ item }">
            {{ item.notes || 'Sin notas' }}
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="info"
              @click="viewIngreso(item)"
              :disabled="!item"
            ></v-btn>
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="primary"
              @click="editIngreso(item)"
              :disabled="!item || !['Pendiente'].includes(item.status)"
            ></v-btn>
            <v-btn
              icon="mdi-package-down"
              size="small"
              variant="text"
              color="success"
              @click="createStorageList(item)"
              :disabled="!item || item.status !== 'Pendiente'"
              title="Crear Lista de Almacenamiento"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="deleteIngreso(item)"
              :disabled="!item || !['Pendiente'].includes(item.status)"
            ></v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon icon="mdi-alert" color="warning" size="large"></v-icon>
              <p class="text-subtitle-1 mt-2">No se encontraron ingresos</p>
            </div>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>
    
    <!-- Modal de Nuevo Ingreso -->
    <v-dialog v-model="showCreateModal" max-width="1000" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          Nuevo Ingreso
          <v-chip
            :color="simplifiedMode ? 'success' : 'primary'"
            label
            size="small"
          >
            {{ simplifiedMode ? 'Modo Simplificado' : 'Modo Tradicional' }}
          </v-chip>
        </v-card-title>
        
        <!-- Toggle de modo de ingreso -->
        <v-card-text class="pb-0">
          <v-alert 
            :type="simplifiedMode ? 'success' : 'info'"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <v-icon class="mr-2">{{ simplifiedMode ? 'mdi-lightning-bolt' : 'mdi-account-hard-hat' }}</v-icon>
                <strong>
                  {{ simplifiedMode ? 'Modo Simplificado:' : 'Modo Tradicional:' }}
                </strong>
                {{ simplifiedMode 
                   ? 'Los productos se agregan directamente al inventario' 
                   : 'Se asigna trabajo de almacenamiento al bodeguero' }}
              </div>
              <v-switch
                v-model="simplifiedMode"
                :label="simplifiedMode ? 'Simplificado' : 'Tradicional'"
                color="success"
                hide-details
                density="compact"
                inset
              ></v-switch>
            </div>
          </v-alert>
        </v-card-text>
        
        <v-card-text>
          <v-form ref="createForm" @submit.prevent="submitIngreso">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newIngreso.order_number"
                  label="Número de Orden"
                  :rules="[v => !!v || 'El número de orden es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="newIngreso.supplier"
                  label="Proveedor"
                  placeholder="Nombre del proveedor"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newIngreso.warehouse_id"
                  :items="warehouses"
                  item-title="name"
                  item-value="id"
                  label="Bodega"
                  :rules="[v => !!v || 'La bodega es requerida']"
                  @update:model-value="loadZones"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="newIngreso.zone_id"
                  :items="filteredZones"
                  item-title="name"
                  item-value="id"
                  label="Zona"
                  :rules="[v => !!v || 'La zona es requerida']"
                  :disabled="!newIngreso.warehouse_id"
                  required
                ></v-select>
              </v-col>
            </v-row>

            <v-textarea
              v-model="newIngreso.notes"
              label="Notas"
              rows="3"
            ></v-textarea>

            <v-divider class="my-4"></v-divider>

            <h3 class="text-h6 mb-4">Productos</h3>
            <v-card
              v-for="(item, index) in newIngreso.items"
              :key="index"
              variant="outlined"
              class="ingreso-item-card mb-3"
              :class="{ 'ingreso-item-card--gs1': item.auto_filled && item.barcode_type === 'GS1' }"
            >
              <!-- Encabezado: producto + acciones -->
              <div class="ingreso-item-card__header">
                <div class="ingreso-item-card__index">{{ index + 1 }}</div>
                <v-autocomplete
                  v-model="item.product_id"
                  :items="getFilteredProducts(index)"
                  item-title="name"
                  item-value="id"
                  label="Producto"
                  variant="outlined"
                  density="comfortable"
                  class="ingreso-item-card__product"
                  :rules="[v => !!v || 'El producto es requerido']"
                  required
                  clearable
                  :search="item.productSearch"
                  @update:search="(search) => handleProductSearch(search, index)"
                  @update:model-value="handleProductSelection(index, $event)"
                  :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                  :no-data-text="item.productSearch && item.productSearch.length >= 2 ? 'No se encontraron productos' : 'Escribe GTIN o nombre para buscar...'"
                  :custom-filter="() => true"
                  :hint="itemProductHint(item)"
                  :persistent-hint="!!item.product_id"
                  auto-select-first
                  hide-no-data
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                      <v-list-item-subtitle>GTIN: {{ item.raw.gtin || item.raw.barcode || item.raw.sku }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>

                <div class="ingreso-item-card__actions">
                  <v-chip
                    v-if="item.auto_filled && item.barcode_type === 'GS1'"
                    size="small"
                    color="success"
                    variant="tonal"
                    prepend-icon="mdi-barcode"
                    class="mr-1"
                  >
                    GS1
                  </v-chip>
                  <v-btn
                    icon="mdi-barcode-scan"
                    color="primary"
                    variant="tonal"
                    size="small"
                    title="Escanear con cámara"
                    @click="startScan(index)"
                  ></v-btn>
                  <v-btn
                    v-if="item.product_id && getProductBarcode(item.product_id) && isPrintableBarcode(getProductBarcode(item.product_id))"
                    icon="mdi-printer"
                    color="success"
                    variant="tonal"
                    size="small"
                    :title="`Imprimir etiqueta GS1: ${getProductBarcode(item.product_id)}`"
                    @click="showPrintProductDialog(item.product_id, item.quantity, item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-delete-outline"
                    color="error"
                    variant="tonal"
                    size="small"
                    title="Quitar producto"
                    :disabled="newIngreso.items.length === 1"
                    @click="removeItem(index)"
                  ></v-btn>
                </div>
              </div>

              <!-- Datos de la línea: cantidad, lote, serie, vencimiento -->
              <v-row dense class="ingreso-item-card__fields">
                <v-col cols="6" sm="3" md="2">
                  <v-text-field
                    v-model.number="item.quantity"
                    type="number"
                    label="Cantidad"
                    variant="outlined"
                    density="comfortable"
                    min="1"
                    :rules="[
                      v => !!v || 'La cantidad es requerida',
                      v => v > 0 || 'La cantidad debe ser mayor a 0'
                    ]"
                    required
                    @update:model-value="handleQuantityChange(index)"
                  ></v-text-field>
                </v-col>
                <v-col cols="6" sm="4" :md="item.quantity === 1 ? 4 : 5">
                  <v-text-field
                    v-model="item.lot_number"
                    label="Lote (10)"
                    placeholder="Lote"
                    variant="outlined"
                    density="comfortable"
                    clearable
                    maxlength="20"
                    :rules="gs1Max20Rules"
                    :prepend-inner-icon="item.auto_filled && item.lot_number ? 'mdi-barcode' : undefined"
                    :color="item.auto_filled && item.lot_number ? 'success' : undefined"
                    :hint="item.auto_filled && item.lot_number ? 'Desde el código GS1' : undefined"
                    :persistent-hint="!!(item.auto_filled && item.lot_number)"
                  ></v-text-field>
                </v-col>
                <!-- Serie (21): solo tiene sentido para una unidad -->
                <v-col v-if="item.quantity === 1" cols="12" sm="5" md="3">
                  <v-text-field
                    v-model="item.serial_number"
                    label="N° de serie (21)"
                    placeholder="Serie"
                    variant="outlined"
                    density="comfortable"
                    clearable
                    maxlength="20"
                    :rules="gs1Max20Rules"
                    :prepend-inner-icon="item.auto_filled && item.serial_number ? 'mdi-barcode' : undefined"
                    :color="item.auto_filled && item.serial_number ? 'success' : undefined"
                    :hint="item.auto_filled && item.serial_number ? 'Desde el código GS1' : undefined"
                    :persistent-hint="!!(item.auto_filled && item.serial_number)"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" :md="item.quantity === 1 ? 3 : 5">
                  <v-text-field
                    v-model="item.expiry_date"
                    label="Vencimiento (17)"
                    type="date"
                    variant="outlined"
                    density="comfortable"
                    :rules="[v => !!v || 'La fecha de vencimiento es requerida']"
                    required
                    :prepend-inner-icon="isGs1FilledExpiry(item) ? 'mdi-barcode' : undefined"
                    :color="isGs1FilledExpiry(item) ? 'success' : undefined"
                    :hint="isGs1FilledExpiry(item) ? 'Desde el código GS1' : undefined"
                    :persistent-hint="isGs1FilledExpiry(item)"
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- Otras fechas GS1 (11/13/15): visibles si vienen del código o si el usuario las despliega -->
              <div class="ingreso-item-card__extra">
                <v-btn
                  variant="text"
                  size="small"
                  color="grey-darken-1"
                  :prepend-icon="isExtraDatesOpen(item) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  @click="toggleExtraDates(item)"
                >
                  Otras fechas GS1
                  <v-chip
                    v-if="filledExtraDates(item).length"
                    size="x-small"
                    color="success"
                    variant="tonal"
                    class="ml-2"
                  >
                    {{ filledExtraDates(item).map(d => d.label).join(' · ') }}
                  </v-chip>
                </v-btn>
                <v-expand-transition>
                  <v-row v-show="isExtraDatesOpen(item)" dense class="mt-1">
                    <v-col v-for="d in gs1ExtraDates" :key="d.key" cols="12" sm="4">
                      <v-text-field
                        v-model="item[d.key]"
                        :label="`${d.label} (${d.ai})`"
                        type="date"
                        variant="outlined"
                        density="compact"
                        clearable
                        :prepend-inner-icon="item.auto_filled && item[d.key] ? 'mdi-barcode' : undefined"
                        :color="item.auto_filled && item[d.key] ? 'success' : undefined"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-expand-transition>
              </div>

              <!-- Código leído tal cual, para referencia -->
              <div v-if="item.barcode_type === 'GS1' && item.barcode_original" class="ingreso-item-card__raw">
                <v-icon size="14" class="mr-1">mdi-barcode</v-icon>
                <span class="text-caption text-grey-darken-1">Código leído:</span>
                <code>{{ item.barcode_original }}</code>
              </div>
            </v-card>

            <!-- Sección de Escaneo Múltiple -->
            <div v-if="isMultipleScanMode" class="mt-4 pa-4 border rounded">
              <div class="d-flex justify-space-between align-center mb-3">
                <h6 class="text-h6 d-flex align-center">
                  <v-icon icon="mdi-barcode-scan" class="mr-2"></v-icon>
                  Modo Escaneo Múltiple
                </h6>
                <v-chip
                  v-if="scannedProducts.length > 0"
                  color="primary"
                  variant="tonal"
                  size="small"
                >
                  {{ scannedProducts.length }} productos | {{ scannedProducts.reduce((sum, p) => sum + p.quantity, 0) }} unidades
                </v-chip>
              </div>

              <v-alert
                type="info"
                variant="tonal"
                class="mb-3"
                density="compact"
              >
                <div class="text-body-2">
                  Use su lector de códigos de barras o escriba manualmente. Los productos se agregarán automáticamente a la lista.
                </div>
              </v-alert>

              <!-- Campo de entrada para códigos de barras -->
              <v-text-field
                ref="multipleScanInput"
                v-model="multipleScanInput"
                label="Escanee aquí o escriba el código de barras"
                prepend-inner-icon="mdi-barcode-scan"
                variant="outlined"
                density="comfortable"
                autofocus
                clearable
                @keydown.enter="processScannedBarcode"
                @input="handleBarcodeInput"
                :loading="scanStatus.type === 'loading'"
                :color="scanStatus.type === 'success' ? 'success' : scanStatus.type === 'error' ? 'error' : 'primary'"
              >
                <template v-slot:append>
                  <v-btn
                    v-if="scannedProducts.length > 0"
                    icon="mdi-undo"
                    color="warning"
                    variant="text"
                    @click="undoLastScan"
                    :title="'Deshacer: ' + (lastScannedProduct ? lastScannedProduct.name : '')"
                  ></v-btn>
                </template>
              </v-text-field>

              <!-- Mensaje de estado -->
              <v-alert
                v-if="scanStatus.message"
                :type="scanStatus.type === 'loading' ? 'info' : scanStatus.type"
                variant="tonal"
                class="mt-2"
                density="compact"
                dismissible
              >
                {{ scanStatus.message }}
              </v-alert>

              <!-- Lista compacta de productos escaneados -->
              <div v-if="scannedProducts.length > 0" class="mt-3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <small class="text-grey">Últimos productos escaneados:</small>
                  <v-btn
                    v-if="scannedProducts.length > 0"
                    color="warning"
                    variant="text"
                    size="small"
                    @click="clearScannedProducts"
                  >
                    Limpiar Todo
                  </v-btn>
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <v-chip
                    v-for="(product, index) in scannedProducts.slice(-5)"
                    :key="product.id + '-' + index"
                    color="success"
                    variant="tonal"
                    size="small"
                  >
                    {{ product.name }} ({{ product.quantity }})
                  </v-chip>
                  <v-chip
                    v-if="scannedProducts.length > 5"
                    color="grey"
                    variant="text"
                    size="small"
                  >
                    +{{ scannedProducts.length - 5 }} más
                  </v-chip>
                </div>
              </div>
            </div>

            <div class="d-flex justify-space-between align-center mt-4">
              <v-btn
                :color="isMultipleScanMode ? 'success' : 'secondary'"
                :variant="isMultipleScanMode ? 'elevated' : 'outlined'"
                :prepend-icon="isMultipleScanMode ? 'mdi-check' : 'mdi-barcode-scan'"
                @click="toggleMultipleScanMode"
              >
                {{ isMultipleScanMode ? 'Finalizar Escaneo' : 'Escanear Múltiples' }}
              </v-btn>
              <v-btn
                color="primary"
                variant="text"
                prepend-icon="mdi-plus"
                @click="addItem"
                :disabled="isMultipleScanMode"
              >
                Agregar Producto
              </v-btn>
            </div>

            <v-alert
              v-if="!hasCapacity"
              type="warning"
              class="mt-4"
            >
              La zona seleccionada no tiene capacidad suficiente para este ingreso
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="closeCreateModal">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            :disabled="!hasCapacity"
            @click="submitIngreso"
          >
            Crear Ingreso
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Editar Ingreso -->
    <v-dialog v-model="showEditModal" max-width="1000" persistent>
      <v-card>
        <v-card-title>{{ isEditingIngreso ? 'Editar Ingreso' : 'Nuevo Ingreso' }}</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="saveEditedIngreso">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.reference"
                  label="Número de Orden"
                  :rules="[v => !!v || 'El número de orden es requerido']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.supplier"
                  label="Proveedor"
                  placeholder="Nombre del proveedor"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editingIngreso.status"
                  :items="statusOptions"
                  label="Estado"
                  :rules="[v => !!v || 'El estado es requerido']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editingIngreso.created_at"
                  label="Fecha de Creación"
                  type="date"
                  readonly
                ></v-text-field>
              </v-col>
            </v-row>

            <v-textarea
              v-model="editingIngreso.notes"
              label="Notas"
              rows="3"
            ></v-textarea>

            <v-divider class="my-4"></v-divider>

            <h3 class="text-h6 mb-4">Productos</h3>
            <v-row v-for="(item, index) in editingIngreso.details" :key="`edit-${index}`" class="mb-2">
              <v-col cols="12" md="3">
                <div class="d-flex align-center">
                  <v-autocomplete
                    v-model="item.product_id"
                    :items="getFilteredProductsEdit(index)"
                    item-title="name"
                    item-value="id"
                    label="Producto"
                    :rules="[v => !!v || 'El producto es requerido']"
                    required
                    clearable
                    :search="item.productSearch"
                    @update:search="(search) => handleProductSearchEdit(search, index)"
                    @update:model-value="handleProductSelectionEdit(index, $event)"
                    :menu-props="{ maxHeight: 300, closeOnContentClick: false }"
                    :custom-filter="() => true"
                    auto-select-first
                    hide-no-data
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item v-bind="props">
                        <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                        <v-list-item-subtitle>GTIN: {{ item.raw.gtin || item.raw.barcode || item.raw.sku }}</v-list-item-subtitle>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                  <v-btn
                    icon="mdi-barcode-scan"
                    color="primary"
                    class="ms-2"
                    @click="startScanEdit(index)"
                  ></v-btn>
                  <!-- Botón de impresión de etiquetas GS1 en modo edición -->
                  <v-btn
                    v-if="item.product_id && getProductBarcode(item.product_id) && isPrintableBarcode(getProductBarcode(item.product_id))"
                    icon="mdi-printer"
                    color="success"
                    class="ms-1"
                    @click="showPrintProductDialog(item.product_id, item.quantity, item)"
                    :title="`Imprimir etiqueta GS1: ${getProductBarcode(item.product_id)}`"
                  ></v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Cantidad"
                  min="1"
                  :rules="[
                    v => !!v || 'La cantidad es requerida',
                    v => v > 0 || 'La cantidad debe ser mayor a 0'
                  ]"
                  required
                  @update:model-value="handleQuantityChangeEdit(index)"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.lot_number"
                  label="Lote"
                  placeholder="Lote"
                  clearable
                  maxlength="20"
                  :rules="gs1Max20Rules"
                ></v-text-field>
              </v-col>
              <!-- Campo S/N (Serial Number) - Solo visible cuando cantidad = 1 -->
              <v-col v-if="item.quantity === 1" cols="12" md="2">
                <v-text-field
                  v-model="item.serial_number"
                  label="S/N"
                  placeholder="S/N"
                  clearable
                  maxlength="20"
                  :rules="gs1Max20Rules"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="item.expiry_date"
                  label="Fecha de Vencimiento"
                  type="date"
                  :rules="[v => !!v || 'La fecha de vencimiento es requerida']"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="1" class="d-flex align-center">
                <v-btn
                  icon="mdi-delete"
                  color="error"
                  variant="text"
                  @click="removeEditItem(index)"
                  :disabled="editingIngreso.details.length === 1"
                ></v-btn>
              </v-col>
            </v-row>

            <div class="d-flex justify-end mt-4">
              <v-btn
                color="primary"
                variant="text"
                prepend-icon="mdi-plus"
                @click="addEditItem"
              >
                Agregar Producto
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showEditModal = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="saveEditedIngreso"
          >
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Detalles de Ingreso -->
    <v-dialog v-model="showDetailsModal" max-width="800">
      <v-card>
        <v-card-title>Detalles del Ingreso</v-card-title>
        <v-card-text v-if="selectedIngreso">
          <v-row>
            <v-col cols="12" md="6">
              <div class="detail-group">
                <label>Número de Orden:</label>
                <p>{{ selectedIngreso.reference }}</p>
              </div>
              <div class="detail-group">
                <label>Fecha:</label>
                <p>{{ formatDate(selectedIngreso.created_at) }}</p>
              </div>
              <div class="detail-group">
                <label>Proveedor:</label>
                <p>{{ selectedIngreso.supplier || 'No especificado' }}</p>
              </div>
              <div class="detail-group">
                <label>Estado:</label>
                <v-chip :color="getStatusColor(selectedIngreso.status)" text-color="white">
                  {{ getStatusText(selectedIngreso.status) }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-group">
                <label>Tipo:</label>
                <p>{{ selectedIngreso.type }}</p>
              </div>
              <div class="detail-group">
                <label>Notas:</label>
                <p>{{ selectedIngreso.notes || 'Sin notas' }}</p>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6 mb-4">Productos</h3>
          <v-table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Cantidad</th>
                <th>Lote</th>
                <th>S/N</th>
                <th>Fecha de Vencimiento</th>
                <th>Etiqueta</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedIngreso.details" :key="item.id">
                <td>{{ item.product ? item.product.name : 'Producto no encontrado' }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.lot_number || '' }}</td>
                <td>{{ item.serial_number || '' }}</td>
                <td>{{ formatDate(item.expiry_date) }}</td>
                <td>
                  <!-- Re-imprimir el código del ítem (GTIN + lote/venc/serie guardados) -->
                  <v-btn
                    v-if="detailProductId(item) && getProductBarcode(detailProductId(item)) && isPrintableBarcode(getProductBarcode(detailProductId(item)))"
                    icon="mdi-printer"
                    color="success"
                    size="small"
                    variant="text"
                    @click="showPrintProductDialog(detailProductId(item), item.quantity, item)"
                    :title="`Imprimir etiqueta: ${getProductBarcode(detailProductId(item))}`"
                  ></v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showDetailsModal = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Modal de Confirmación de Eliminación -->
    <v-dialog v-model="showDeleteModal" max-width="500">
      <v-card>
        <v-card-title>Confirmar Eliminación</v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar este ingreso? Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showDeleteModal = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="confirmDeleteIngreso">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Escáner -->
    <v-dialog v-model="showBarcodeScanner" max-width="500">
      <v-card>
        <v-card-title>Escanear Código de Barras</v-card-title>
        <v-card-text>
          <div class="text-center">
            <video ref="videoElement" class="barcode-scanner"></video>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="stopScanner"
          >
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Configuración de Lista de Almacenamiento -->
    <v-dialog v-model="showStorageConfigDialog" max-width="600">
      <v-card>
        <v-card-title>
          <v-icon icon="mdi-cog" class="mr-2"></v-icon>
          Configurar Lista de Almacenamiento
        </v-card-title>
        <v-card-text>
          <div v-if="selectedIngreso" class="mb-4">
            <h6 class="text-h6 mb-2">Ingreso: {{ selectedIngreso.reference }}</h6>
            <p class="text-body-2 text-grey">
              Proveedor: {{ selectedIngreso.supplier || 'No especificado' }}
            </p>
          </div>
          
          <v-divider class="mb-4"></v-divider>
          
          <div class="switch-container">
            <v-switch
              v-model="storageConfig.pieceByPieceEnabled"
              label="Habilitar Almacenamiento Piece-by-Piece"
              color="primary"
              inset
              hide-details
            ></v-switch>
            <p class="text-body-2 text-grey mt-1 ml-12">
              El operario deberá escanear cada producto individualmente antes de almacenar
            </p>
          </div>
          
          <v-alert
            v-if="storageConfig.pieceByPieceEnabled"
            type="info"
            variant="tonal"
            class="mt-4"
          >
            <div class="text-body-2">
              <strong>Almacenamiento Piece-by-Piece Habilitado:</strong>
              <ul class="mt-2">
                <li>El operario deberá escanear cada unidad individual del producto</li>
                <li>Se validará que el código de barras coincida con el producto</li>
                <li>El progreso se actualizará automáticamente cada 5 segundos</li>
                <li>Solo se podrá completar cuando todas las unidades estén escaneadas</li>
              </ul>
            </div>
          </v-alert>
          
          <v-alert
            v-else
            type="warning"
            variant="tonal"
            class="mt-4"
          >
            <div class="text-body-2">
              <strong>Almacenamiento Tradicional:</strong>
              <br>
              El operario escaneará la ubicación y confirmará el producto una sola vez por item.
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showStorageConfigDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            :loading="submitting"
            @click="confirmCreateStorageList"
            prepend-icon="mdi-plus"
          >
            Crear Lista
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Impresión de Códigos de Barras -->
    <v-dialog v-model="showPrintDialog" max-width="600">
      <v-card>
        <v-card-title>
          Imprimir Código de Barras
          <v-chip color="success" size="small" class="ml-2">
            {{ selectedSymbologyTitle }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <div class="text-h6 mb-2">
                {{ selectedProductForPrint?.name }}
              </div>
              <div class="text-subtitle-2 mb-2">
                GTIN a imprimir: <strong>{{ printTargetGtin }}</strong>
                <span v-if="printSettings.target === 'case'" class="text-medium-emphasis">
                  (caja · {{ selectedProductForPrint?.units_per_case || 1 }} un./caja)
                </span>
              </div>
            </v-col>
          </v-row>

          <!-- Qué etiqueta: unidad comercial o unidad de despacho (caja) -->
          <v-row>
            <v-col cols="12" :md="canPrintCase ? 7 : 12">
              <v-radio-group
                v-model="printSettings.target"
                inline
                density="compact"
                hide-details
                label="Etiqueta para"
              >
                <v-radio
                  :label="`GTIN comercial (unidad) · ${selectedProductForPrint?.barcode}`"
                  value="unit"
                ></v-radio>
                <v-radio
                  v-if="canPrintCase"
                  :label="`GTIN de despacho (caja) · ${selectedProductForPrint?.case_gtin}`"
                  value="case"
                ></v-radio>
              </v-radio-group>
              <div v-if="!canPrintCase" class="text-caption text-medium-emphasis mt-1">
                Este producto no tiene GTIN de caja (se define al editar el producto)
              </div>
            </v-col>
            <v-col cols="12" :md="canPrintCase ? 5 : 12">
              <v-checkbox
                v-model="printSettings.printAll"
                density="compact"
                hide-details
                :label="printSettings.target === 'case'
                  ? `Una etiqueta por caja (${printCaseCount})`
                  : `Una etiqueta por unidad (${selectedProductForPrint?.quantity || 1})`"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.symbology"
                :items="availableSymbologies"
                label="Simbología GS1"
                hint="GS1-128 y DataMatrix codifican GTIN + lote + vencimiento"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.printerType"
                :items="[
                  { title: 'Impresora Estándar (A4)', value: 'standard' },
                  { title: 'Impresora de Etiquetas (Zebra/Térmica)', value: 'label' }
                ]"
                label="Tipo de Impresora"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="usesGS1ElementString">
            <v-col cols="12">
              <v-alert type="info" variant="tonal" density="compact">
                Se codificará: <code>{{ gs1ElementStringPreview }}</code>
              </v-alert>
              <!-- Orden de los AIs: GS1 no exige uno; mover (10) al comienzo o
                   al medio demuestra ante el evaluador que el lector parsea
                   por FNC1 y no por posición -->
              <div v-if="printAiList.length > 1" class="d-flex align-center flex-wrap ga-1 mt-2">
                <span class="text-caption text-medium-emphasis mr-1">Orden de los AIs:</span>
                <v-chip v-for="(ai, i) in printAiList" :key="ai" size="small" variant="outlined" color="indigo" class="pr-1">
                  ({{ ai }})
                  <v-btn icon="mdi-chevron-left" size="x-small" variant="text" density="compact" :disabled="i === 0"
                         title="Mover antes" @click="moveAi(i, -1)"></v-btn>
                  <v-btn icon="mdi-chevron-right" size="x-small" variant="text" density="compact" :disabled="i === printAiList.length - 1"
                         title="Mover después" @click="moveAi(i, 1)"></v-btn>
                </v-chip>
                <v-btn v-if="printSettings.aiOrder.length" size="x-small" variant="text" @click="resetAiOrder">Orden natural</v-btn>
              </div>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'standard'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelsPerRow"
                :items="[1, 2, 3, 4]"
                label="Etiquetas por fila"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelSize"
                :items="[
                  { title: 'Pequeño (2x1 cm)', value: 'small' },
                  { title: 'Mediano (4x2 cm)', value: 'medium' },
                  { title: 'Grande (6x3 cm)', value: 'large' }
                ]"
                label="Tamaño de etiqueta"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="printSettings.printerType === 'label'">
            <v-col cols="12" md="6">
              <v-select
                v-model="printSettings.labelFormat"
                :items="[
                  { title: '2x1 pulgadas', value: '2x1' },
                  { title: '4x2 pulgadas', value: '4x2' },
                  { title: '4x6 pulgadas', value: '4x6' }
                ]"
                label="Formato de etiqueta"
              ></v-select>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="printSettings.dpi"
                type="number"
                label="DPI"
                hint="Resolución de la impresora (203, 300, 600)"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Información automática de cantidad -->
          <v-row>
            <v-col cols="12">
              <v-alert type="info" variant="tonal" class="mb-3" density="compact">
                <strong>Cantidad a imprimir:</strong> {{ printCopies }} etiqueta(s)
                <br>
                <small v-if="printSettings.printAll">
                  {{ printSettings.target === 'case'
                    ? `${selectedProductForPrint?.quantity} un. ÷ ${selectedProductForPrint?.units_per_case || 1} un./caja = ${printCaseCount} caja(s)`
                    : `Una por cada unidad de la línea (${selectedProductForPrint?.quantity})` }}
                </small>
                <small v-else>Marca "Una etiqueta por {{ printSettings.target === 'case' ? 'caja' : 'unidad' }}" para imprimir todas</small>
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="text-subtitle-2 mb-2">Vista Previa:</div>
          <div class="print-preview-container" :class="printSettings.printerType">
            <div 
              v-for="copy in printCopies"
              :key="copy"
              class="barcode-label preview-label"
              :class="printSettings.labelSize"
            >
              <div class="barcode-visual">
                <canvas 
                  :ref="'previewCanvas' + copy"
                  class="preview-barcode-canvas"
                ></canvas>
              </div>
              <div class="barcode-text">{{ usesGS1ElementString ? gs1ElementStringPreview : printTargetGtin }}</div>
            </div>
          </div>
          <ZebraPrinterSelect v-if="printSettings.symbology !== 'gs1qrcode'" class="mt-3" />
        </v-card-text>
        <v-card-actions class="flex-wrap justify-end ga-1">
          <v-btn color="grey" variant="text" size="small" @click="showPrintDialog = false">
            Cancelar
          </v-btn>
          <v-btn
            v-if="printSettings.symbology !== 'gs1qrcode'"
            color="teal" variant="flat" size="small"
            prepend-icon="mdi-printer-wireless"
            :loading="zebraPrinting"
            @click="printBarcodeZebra"
            title="ZPL nativo vía Browser Print: la Zebra dibuja el símbolo exacto por dot (calidad de verificación)"
          >
            Imprimir en Zebra
          </v-btn>
          <v-btn color="primary" size="small" @click="printProductBarcode" :loading="printing"
                 title="Impresión por navegador (rasterizada) — usar solo si no está la Zebra">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de confirmación para crear producto no encontrado -->
    <v-dialog v-model="showCreateProductConfirm" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="warning" class="mr-2">mdi-package-variant-plus</v-icon>
          Producto No Encontrado
        </v-card-title>
        <v-card-text>
          <v-alert type="warning" variant="tonal" class="mb-4">
            <div class="font-weight-bold">Código escaneado:</div>
            <div class="text-mono mt-1">{{ lastScannedCode }}</div>
            <div v-if="lastExtractedGTIN !== lastScannedCode" class="mt-2">
              <div class="font-weight-bold">GTIN extraído:</div>
              <div class="text-mono">{{ lastExtractedGTIN }}</div>
            </div>
          </v-alert>
          <p class="text-body-1">
            No se encontró un producto registrado con este código de barras. 
            ¿Deseas crear un nuevo producto?
          </p>
          <v-alert type="info" variant="text" class="mt-3" density="compact">
            <v-icon class="mr-2">mdi-information</v-icon>
            Se abrirá el formulario para crear un producto nuevo y luego se agregará automáticamente a este ingreso.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            color="grey" 
            variant="text" 
            @click="cancelCreateProduct"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="primary" 
            @click="confirmCreateProduct"
            prepend-icon="mdi-plus"
          >
            Crear Producto
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de creación de nuevo producto -->
    <ProductDialog
      v-model="showNewProductDialog"
      :product="newProductData"
      mode="add"
      @save="saveNewProduct"
    />

  </div>
  <!-- Validador GS1 (checklist 5.2): escanear o pegar un código → desglose + veredicto -->
  <v-dialog v-model="validatorDialog" max-width="700">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="teal" class="mr-2">mdi-check-decagram</v-icon>
        Validador GS1
      </v-card-title>
      <v-card-subtitle>
        Escanea una etiqueta o pega el contenido del código. Se valida estructura,
        dígitos verificadores de las claves y fechas reales de cada AI.
      </v-card-subtitle>
      <v-card-text>
        <v-text-field
          v-model="validatorInput"
          label="Escanear o pegar código GS1"
          prepend-inner-icon="mdi-barcode-scan"
          autofocus clearable
          @keyup.enter="runValidator"
          hint="Ej: (02)17795678901213(10)AAB123(17)201231(37)20 — acepta con o sin paréntesis, GS1-128, DataMatrix, EAN"
          persistent-hint
          class="mb-3"
        ></v-text-field>

        <template v-if="validatorResult">
          <v-alert :type="validatorResult.errors.length === 0 ? 'success' : 'error'" variant="tonal" class="mb-3">
            <b>{{ validatorResult.errors.length === 0 ? 'CÓDIGO VÁLIDO GS1' : 'CÓDIGO CON ERRORES' }}</b>
            <div v-for="(err, i) in validatorResult.errors" :key="i" class="text-body-2 mt-1">• {{ err }}</div>
          </v-alert>

          <!-- Simbología leída (AIM del lector) + presencia de FNC1 (checklist 3.2/3.4) -->
          <div class="mb-3 d-flex flex-wrap ga-2" v-if="validatorResult.symbology || validatorResult.hadFNC1">
            <v-chip v-if="validatorResult.symbology" size="small"
                    :color="validatorResult.symbology.includes('NO GS1') ? 'error' : 'teal'" variant="tonal">
              <v-icon start size="small">mdi-barcode</v-icon>
              Simbología leída: {{ validatorResult.symbology }} (AIM {{ validatorResult.aim }})
            </v-chip>
            <v-chip v-if="validatorResult.hadFNC1" size="small" color="indigo" variant="tonal">
              <v-icon start size="small">mdi-format-vertical-align-center</v-icon>
              Separadores FNC1 detectados
            </v-chip>
          </div>

          <!-- Secuencia completa con los FNC1 en su posición (checklist 3.4) -->
          <div v-if="validatorResult.sequence" class="mb-3">
            <div class="text-caption text-grey">
              Secuencia interpretada — el separador FNC1 se transmite como el carácter GS (ASCII 29):
            </div>
            <code class="text-body-2">{{ validatorResult.sequence }}</code>
          </div>

          <v-alert v-if="validatorResult.aim === ']C1'" type="info" variant="tonal" density="compact" class="mb-3">
            Un GS1-128 llega con AIM <b>]C1</b> (FNC1 en primera posición). Los motores de
            escaneo móviles suelen rotularlo "CODE_128" (la familia física de la simbología),
            pero el ]C1 y los separadores GS confirman que es GS1-128.
          </v-alert>

          <v-table density="compact" v-if="validatorResult.items.length">
            <thead><tr><th>AI</th><th>Campo</th><th>Valor</th><th>FNC1</th></tr></thead>
            <tbody>
              <tr v-for="(it, i) in validatorResult.items" :key="i">
                <td><code>({{ it.ai }})</code></td>
                <td>{{ it.name }}</td>
                <td>{{ it.value }}</td>
                <td>
                  <v-chip v-if="it.fnc1After" size="x-small" color="indigo" variant="tonal">⟨FNC1·GS⟩</v-chip>
                  <span v-else-if="it.variable" class="text-caption text-grey">fin/AI sig.</span>
                  <span v-else class="text-caption text-grey">— (largo fijo)</span>
                </td>
              </tr>
            </tbody>
          </v-table>
          <div v-if="!validatorResult.items.length" class="text-grey">No se reconocieron AIs en el código.</div>
        </template>
        <div class="text-caption text-grey mt-3">
          Nota: las etiquetas que imprime el sistema se validan también al generarse
          (estructura y dígito verificador de cada simbología).
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="validatorDialog = false">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ZoneCapacityManager from '@/components/ZoneCapacityManager.vue'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'
import ProductDialog from '@/components/inventory/ProductDialog.vue'
import {
  BrowserMultiFormatReader,
  DecodeHintType,
  BarcodeFormat,
  BinaryBitmap,
  HybridBinarizer,
  HTMLCanvasElementLuminanceSource,
  NotFoundException
} from '@zxing/library'
import api from '@/api'
import { expandUPCE, isValidGS1Key, gs1CheckDigit } from '@/utils/gs1Validation'
import { PRINT_SYMBOLOGIES, AI_SYMBOLOGIES, renderBarcodeToCanvas, renderBarcodeToDataURL, symbologyAvailable, buildGS1ElementString, elementStringAIs } from '@/utils/gs1Barcode'
import { buildSymbologyPrintZPL } from '@/utils/zplLabel'
import { databarZplBars } from '@/utils/databarZpl'
import { printZplViaBrowserPrint, resolveBrowserPrintTarget } from '@/utils/browserPrint'
import ZebraPrinterSelect from '@/components/ZebraPrinterSelect.vue'

// Fechas GS1 opcionales por línea, además del vencimiento (17/15 → expiry_date):
// se muestran en la card del producto y viajan al backend con estos nombres
const GS1_EXTRA_DATES = [
  { key: 'production_date',  ai: '11', label: 'Fabricación' },
  { key: 'packaging_date',   ai: '13', label: 'Empaque' },
  { key: 'best_before_date', ai: '15', label: 'Consumo preferente' }
]

// Línea vacía del formulario de nuevo ingreso
function blankIngresoItem() {
  return {
    product_id: null,
    quantity: 1,
    lot_number: '',
    expiry_date: '',
    serial_number: '',
    production_date: '',
    packaging_date: '',
    best_before_date: '',
    productSearch: ''
  }
}

export default {
  name: 'Ingresos',
  components: {
    ZebraPrinterSelect,
    ZoneCapacityManager,
    ResponsiveDataTable,
    ProductDialog
  },
  data() {
    return {
      filters: {
        status: '',
        dateFrom: '',
        dateTo: '',
        search: ''
      },
      isEditingIngreso: false,
      ingresoForm: {
        id: null,
        reference: 'trazalogis',                // ← nueva
        type: 'reception',            // ← fijo para ingresos
        status: 'pending',            // ← inicial
        supplier: '',                 // si lo usas
        reception_date: new Date().toISOString().split('T')[0],           // (lo ignorará el backend)
        destination_location_id: '',
        notes: '',
        items: [
          { product_id: null, quantity: 1, lot_number: '' }
        ]
      },
      selectedIngreso: null,
      selectedZone: null,
      hasCapacity: true,
      totalQuantity: 0,
      showDetailsModal: false,
      showDeleteModal: false,
      showCreateModal: false,
      newIngreso: {
        order_number: '',
        supplier: '',
        warehouse_id: '',
        zone_id: '',
        notes: '',
        items: [
          blankIngresoItem()
        ]
      },
      submitting: false,
      showBarcodeScanner: false,
      codeReader: null,
      currentScanningIndex: null,
      cameraScanBusy: false, // la cámara entrega cuadros continuos: uno a la vez
      // Variables para escaneo múltiple
      isMultipleScanMode: false,
      multipleScanInput: '',
      scannedProducts: [],
      lastScannedProduct: null,
      scanStatus: { type: '', message: '' },
      scanStatusTimer: null,
      showEditModal: false,
      editingIngreso: {
        id: null,
        reference: '',
        supplier: '',
        status: '',
        notes: '',
        created_at: '',
        details: []
      },
      statusOptions: ['Pendiente', 'Completado', 'Cancelado'],
      showStorageConfigDialog: false,
      storageConfig: {
        pieceByPieceEnabled: false
      },
      // Almacenar productos filtrados por índice
      filteredProductsByIndex: {},
      filteredProductsByIndexEdit: {},
      // Variables para impresión de códigos de barras
      showPrintDialog: false,
      selectedProductForPrint: null,
      printing: false,
      zebraPrinting: false,
      printSettings: {
        printerType: 'standard',
        labelsPerRow: 2,
        labelSize: 'medium',
        labelFormat: '4x2',
        dpi: 203,
        copies: 1,
        symbology: 'ean13',
        // Qué GTIN etiquetar: 'unit' = GTIN comercial (unidad), 'case' = GTIN de
        // despacho (caja, solo si el producto lo tiene). Por defecto se imprime
        // UNA etiqueta; con printAll se imprime una por unidad (o por caja).
        target: 'unit',
        printAll: false,
        // Orden de los AIs en el element string ([] = natural). GS1 admite
        // cualquier orden — sirve para demostrar que los lectores parsean bien
        aiOrder: []
      },
      printSymbologies: PRINT_SYMBOLOGIES,
      
      // Variables para creación de productos on-the-fly
      showCreateProductConfirm: false,
      showNewProductDialog: false,
      lastScannedCode: '',
      lastExtractedGTIN: '',
      lastBarcodeAnalysis: null,
      newProductData: {
        sku: '',
        barcode: '',
        codigo_fabrica: '',
        name: '',
        description: '',
        category: '',
        abc_category: '',
        warehouse_id: null,
        zone_id: null,
        location_id: null,
        current_stock: 0,
        min_stock: 0,
        max_stock: 0,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        status: 'Activo'
      },
      
      // Variables para flujo simplificado (directo al inventario)
      simplifiedMode: true, // Por defecto usar modo simplificado
      // true mientras se aplican las preferencias leídas del backend (evita
      // que los watchers las vuelvan a guardar)
      applyingUserPreferences: false,

      // Lote (10) y serie (21) admiten máx. 20 caracteres (checklist GS1 1.5/1.18)
      gs1Max20Rules: [v => !v || String(v).length <= 20 || 'No puede superar 20 caracteres'],
      // Fechas GS1 opcionales mostradas en la card de cada producto
      gs1ExtraDates: GS1_EXTRA_DATES,

      // ========== PARSER GS1 PROFESIONAL - DATOS ==========
      
      // Separador FNC1 (ASCII 29)
      validatorDialog: false,
      validatorInput: '',
      validatorResult: null,

      GS: String.fromCharCode(29),
      
      // Definición de AIs (Application Identifiers) según especificaciones GS1
      AI_DEFS: [
        // ---- Longitud fija ----
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" }, // Identificador del producto
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" }, // Fecha de producción
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" }, // Fecha de empaquetado
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" }, // Fecha de consumo preferente
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" }, // Fecha de vencimiento
        { key: "310", len: 6,  fixed: true,  name: "Net weight (kg, 6d)",  wildcard: true }, // 310x
        { key: "320", len: 6,  fixed: true,  name: "Net weight (lb, 6d)", wildcard: true }, // 320x
        { key: "330", len: 6,  fixed: true,  name: "Gross weight (kg, 6d)", wildcard: true }, // 330x - GS1 REQUERIDO
        { key: "340", len: 6,  fixed: true,  name: "Gross weight (lb, 6d)", wildcard: true }, // 340x - GS1 REQUERIDO
        { key: "390", len: null, fixed: false, name: "Amount w/o curr",   wildcard: true, max: 15 }, // 390x var
        { key: "414", len: 13, fixed: true,  name: "GLN" }, // Global Location Number - GS1 REQUERIDO
        
        // ---- Longitud variable ----
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 }, // Lote
        { key: "21", fixed: false, name: "Serial",    max: 20 }, // Número de serie
        { key: "22", fixed: false, name: "CPV",       max: 20 }, // Consumer Product Variant
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 },
        { key: "90",  fixed: false, name: "Uso interno 90 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "91",  fixed: false, name: "Uso interno 91 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "92",  fixed: false, name: "Uso interno 92 (hasta 30)", max: 30 }, // GS1 REQUERIDO  
        { key: "93",  fixed: false, name: "Uso interno 93 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "94",  fixed: false, name: "Uso interno 94 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "95",  fixed: false, name: "Uso interno 95 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "96",  fixed: false, name: "Uso interno 96 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "97",  fixed: false, name: "Uso interno 97 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "98",  fixed: false, name: "Uso interno 98 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "99",  fixed: false, name: "Uso interno 99 (hasta 30)", max: 30 }, // GS1 REQUERIDO
        { key: "240", fixed: false, name: "Additional ID", max: 30 },
        { key: "241", fixed: false, name: "Customer Part #", max: 30 },
        { key: "242", fixed: false, name: "MTO variant", max: 6 },
        { key: "243", fixed: false, name: "Supplier Part #", max: 30 },
        { key: "253", fixed: false, name: "GDTI + Serial", max: 30 }, // Global Document Type Identifier - GS1 REQUERIDO
        { key: "402", len: 17, fixed: true, name: "GSIN" }, // Global Shipment Identification Number - GS1 REQUERIDO
        { key: "401", fixed: false, name: "GINC", max: 30 }, // Global Identification Number for Consignment - GS1 RECOMENDADO
        { key: "8003", fixed: false, name: "GRAI", max: 30 }, // Global Returnable Asset Identifier - GS1 RECOMENDADO
        { key: "8004", fixed: false, name: "GIAI", max: 30 }, // Global Individual Asset Identifier - GS1 RECOMENDADO
        { key: "8018", len: 18, fixed: true, name: "GSRN" }, // Global Service Relation Number - GS1 RECOMENDADO
      ]
    }
  },
  computed: {
    ...mapGetters({
      ingresos: 'transactions/allIngresos',
      products: 'products/allProducts',
      zones: 'zones/allZones',
      loading: 'transactions/loading',
      warehouses: 'warehouses/allWarehouses'
    }),
    // --- Impresión de simbologías GS1 ---
    // ¿El producto tiene GTIN de despacho (caja) para ofrecer esa etiqueta?
    canPrintCase() {
      const p = this.selectedProductForPrint
      return !!(p?.case_gtin && this.isPrintableBarcode(p.case_gtin))
    },
    // GTIN que va en el (01) según el destino elegido (unidad o caja)
    printTargetGtin() {
      const p = this.selectedProductForPrint
      if (!p) return null
      return (this.printSettings.target === 'case' && this.canPrintCase) ? p.case_gtin : p.barcode
    },
    // Nº de cajas que representa la línea (unidades ÷ unidades por caja, redondeado arriba)
    printCaseCount() {
      const p = this.selectedProductForPrint
      if (!p) return 1
      const upc = parseInt(p.units_per_case, 10) || 1
      return Math.max(1, Math.ceil((p.quantity || 1) / upc))
    },
    // Copias a imprimir: 1 por defecto; "todas" = una por unidad o una por caja
    printCopies() {
      const p = this.selectedProductForPrint
      if (!p || !this.printSettings.printAll) return 1
      return this.printSettings.target === 'case' ? this.printCaseCount : (p.quantity || 1)
    },
    availableSymbologies() {
      const barcode = this.printTargetGtin
      return this.printSymbologies.map(s => ({
        ...s,
        props: { disabled: !symbologyAvailable(s.value, barcode) }
      }))
    },
    selectedSymbologyTitle() {
      const s = this.printSymbologies.find(s => s.value === this.printSettings.symbology)
      return s ? s.title.split(' (')[0] : 'EAN-13'
    },
    usesGS1ElementString() {
      return AI_SYMBOLOGIES.includes(this.printSettings.symbology)
    },
    printBarcodeData() {
      const p = this.selectedProductForPrint
      if (!p) return null
      const gtin = this.printTargetGtin
      // El element string fiel al escaneo solo sirve si su (01) es el GTIN que
      // se va a imprimir (si se escaneó la caja y se imprime la unidad, o al
      // revés, se reconstruye desde lote/vencimiento/serie)
      const sameGtin = (a, b) => String(a || '').replace(/\D/g, '').padStart(14, '0') ===
                                 String(b || '').replace(/\D/g, '').padStart(14, '0')
      const elementString = (p.gs1_element_string && sameGtin(p.gs1_element_string_gtin, gtin))
        ? p.gs1_element_string
        : null
      return {
        gtin,
        lot: p.lot_number || null,
        expiry: p.expiry_date || null,
        productionDate: p.production_date || null,
        serial: p.serial_number || null,
        // Element string fiel al escaneo original (todos los AIs, mismo orden)
        elementString,
        aiOrder: this.printSettings.aiOrder
      }
    },
    gs1ElementStringPreview() {
      return this.printBarcodeData ? buildGS1ElementString(this.printBarcodeData) : ''
    },
    // AIs del element string a imprimir, en el orden actual
    printAiList() {
      return elementStringAIs(this.gs1ElementStringPreview)
    },
    filteredIngresos() {
      if (!this.ingresos || !Array.isArray(this.ingresos)) {
        return [];
      }
      
      let result = this.ingresos
      
      if (this.filters.status) {
        result = result.filter(ingreso => ingreso.status === this.filters.status)
      }
      
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom)
        result = result.filter(ingreso => {
          const ingresoDate = new Date(ingreso.created_at)
          return ingresoDate >= fromDate
        })
      }
      
      if (this.filters.dateTo) {
        const toDate = new Date(this.filters.dateTo)
        toDate.setHours(23, 59, 59, 999) // Final del día
        result = result.filter(ingreso => {
          const ingresoDate = new Date(ingreso.created_at)
          return ingresoDate <= toDate
        })
      }
      
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        result = result.filter(ingreso => {
          return (
            (ingreso.reference && ingreso.reference.toLowerCase().includes(search)) ||
            (ingreso.supplier && ingreso.supplier.toLowerCase().includes(search))
          )
        })
      }

      // Más nuevo primero (el backend los entrega en orden de creación
      // ascendente); desempate por id para ingresos del mismo instante
      const ts = i => new Date(i.created_at || i.date || 0).getTime() || 0
      return [...result].sort((a, b) => (ts(b) - ts(a)) || ((b.id || 0) - (a.id || 0)))
    },
    isIngresoFormValid() {
      return (
        this.ingresoForm.items.length > 0 &&
        this.ingresoForm.items.every(item => item.product_id && item.quantity > 0)
      )
    },
    filteredZones() {
      if (!this.newIngreso.warehouse_id) return []
      return this.zones.filter(zone => zone.warehouse_id === this.newIngreso.warehouse_id)
    },
    availableZones() {
      return this.zones.filter(zone => zone.capacity > 0)
    }
  },
  methods: {
    ...mapActions({
      fetchIngresos: 'transactions/fetchIngresos',
      fetchProducts: 'products/fetchProducts',
      fetchLocations: 'zones/fetchZones',
      fetchZones: 'zones/fetchZones',
      fetchWarehouses: 'warehouses/fetchWarehouses',
      createTransaction: 'transactions/createTransaction',
      updateTransaction: 'transactions/updateTransaction',
      deleteTransaction: 'transactions/deleteTransaction',
      checkZoneCapacityAction: 'zones/checkZoneCapacity'
    }),
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      // Fechas ISO sin hora (YYYY-MM-DD): parsear como fecha LOCAL. new Date('YYYY-MM-DD')
      // asume medianoche UTC y en Chile (UTC-3/-4) retrocede al día anterior.
      const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateString).trim())
      const date = dateOnly
        ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
        : new Date(dateString)
      return date.toLocaleDateString('es-ES')
    },
    getProductName(productId) {
      const product = this.products.find(p => p.id === productId)
      return product ? product.name : `Producto #${productId}`
    },
    // ===== Card de producto en el modal de nuevo ingreso =====
    // SKU y GTIN del producto elegido, bajo el selector (el nombre solo no identifica)
    itemProductHint(item) {
      if (!item.product_id) return undefined
      const p = (this.products || []).find(x => x.id === item.product_id)
      if (!p) return undefined
      const parts = []
      if (p.sku) parts.push(`SKU ${p.sku}`)
      const gtin = p.gtin || p.barcode
      if (gtin && gtin !== p.sku) parts.push(`GTIN ${gtin}`)
      if (p.case_gtin) parts.push(`Caja ${p.case_gtin}`)
      return parts.join('  ·  ') || undefined
    },
    // Vencimiento llenado desde el código (no la fecha de hoy que pone el escaneo simple)
    isGs1FilledExpiry(item) {
      return !!(item.auto_filled && item.expiry_date &&
        item.expiry_date !== new Date().toISOString().split('T')[0])
    },
    filledExtraDates(item) {
      return this.gs1ExtraDates.filter(d => !!item[d.key])
    },
    // Las otras fechas se despliegan solas si el código las traía; si no,
    // quedan plegadas hasta que el usuario las necesite
    isExtraDatesOpen(item) {
      if (typeof item.extraDatesOpen === 'boolean') return item.extraDatesOpen
      return this.filledExtraDates(item).length > 0
    },
    toggleExtraDates(item) {
      item.extraDatesOpen = !this.isExtraDatesOpen(item)
    },
    getLocationName(locationId) {
      const location = this.locations.find(l => l.id === locationId)
      if (!location) return `Ubicación #${locationId}`
      
      const zone = this.zones.find(z => z.id === location.zone_id)
      if (zone) {
        return `${zone.name} (${zone.aisle}-${zone.rack}-${zone.level}-${zone.position})`
      }
      
      return location.code
    },
    getStatusClass(status) {
      const classes = {
        'Completado': 'bg-success',
        'Pendiente': 'bg-warning',
        'Cancelado': 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    },
    applyFilters() {
      // Los filtros se aplican automáticamente a través del computed property
    },
    showNewIngresoModal() {
      this.isEditingIngreso = false
      this.resetIngresoForm()
      this.selectedZone = null
      this.hasCapacity = true
      this.filteredProductsByIndex = {}
      this.showCreateModal = true
    },
    editIngreso(ingreso) {
      this.isEditingIngreso = true
      
      this.editingIngreso = {
        id: ingreso.id,
        reference: ingreso.reference,
        supplier: ingreso.supplier || '',
        status: ingreso.status,
        notes: ingreso.notes || '',
        created_at: new Date(ingreso.created_at).toISOString().split('T')[0],
        details: (ingreso.details || []).map(detail => ({
          id: detail.id,
          product_id: detail.product ? detail.product.id : detail.product_id,
          quantity: detail.quantity,
          lot_number: detail.lot_number || '',
          expiry_date: detail.expiry_date ? detail.expiry_date.split('T')[0] : '',
          serial_number: detail.serial_number || '',
          product: detail.product,
          productSearch: ''
        }))
      }
      
      this.filteredProductsByIndexEdit = {}
      this.showEditModal = true
    },
    viewIngreso(ingreso) {
      this.selectedIngreso = ingreso
      this.showDetailsModal = true
    },
    deleteIngreso(ingreso) {
      this.selectedIngreso = ingreso
      this.showDeleteModal = true
    },
    confirmDeleteIngreso() {
      if (this.selectedIngreso) {
        this.submitting = true
        this.deleteTransaction(this.selectedIngreso.id)
          .then(() => {
            this.showDeleteModal = false
            this.fetchIngresos()
            this.$store.dispatch('showSuccess', 'Ingreso eliminado correctamente')
          })
          .catch(err => {
            console.error('Error al eliminar ingreso:', err)
            this.$store.dispatch('showError', 'Error al eliminar ingreso: ' + (err.message || 'Error desconocido'))
          })
          .finally(() => {
            this.submitting = false
          })
      }
    },
    addProductItem() {
      this.ingresoForm.items.push({
        product_id: '',
        quantity: 1,
        lot_number: ''
      })
    },
    removeProductItem(index) {
      this.ingresoForm.items.splice(index, 1)
      this.checkZoneCapacity()
    },
    resetIngresoForm() {
      this.ingresoForm = {
        id: null,
        supplier: '',
        reception_date: new Date().toISOString().split('T')[0],
        destination_location_id: '',
        notes: '',
        items: [{
          product_id: '',
          quantity: 1,
          lot_number: ''
        }]
      }
      
      // También resetear el formulario de nuevo ingreso
      this.newIngreso = {
        order_number: '',
        supplier: '',
        warehouse_id: '',
        zone_id: '',
        notes: '',
        items: [
          blankIngresoItem()
        ]
      }

      // Resetear modo escaneo múltiple
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
      this.scannedProducts = []
      this.lastScannedProduct = null
      this.scanStatus = { type: '', message: '' }
    },
    saveIngreso() {
      return new Promise((resolve, reject) => {
        // Validar que todos los campos requeridos estén presentes
        if (!this.newIngreso.order_number) {
          reject(new Error('El número de orden es requerido'))
          return
        }

        if (!this.newIngreso.zone_id) {
          reject(new Error('La zona es requerida'))
          return
        }

        if (!this.newIngreso.items.length) {
          reject(new Error('Debe agregar al menos un producto'))
          return
        }

        // Validar que todos los items tengan los campos requeridos
        const invalidItem = this.newIngreso.items.find(
          item => !item.product_id || !item.quantity
        )
        if (invalidItem) {
          reject(new Error('Todos los productos deben tener ID y cantidad'))
          return
        }

        const ingresoData = {
          reference: this.newIngreso.order_number,
          supplier: this.newIngreso.supplier,
          notes: this.newIngreso.notes,
          items: this.newIngreso.items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date,
            production_date: item.production_date || null,   // (11)
            pack_date: item.packaging_date || null,           // (13)
            best_before_date: item.best_before_date || null,  // (15)
            serial_number: item.serial_number || null,
            destination_location_id: this.newIngreso.zone_id
          }))
        }
        
        console.log('📋 Datos del ingreso preparados:', ingresoData)
        console.log('🔄 Modo simplificado:', this.simplifiedMode ? 'SÍ (Directo al inventario)' : 'NO (Con asignación de almacenamiento)')
        
        if (this.simplifiedMode) {
          // Usar endpoint simplificado (directo al inventario)
          this.createSimplifiedReception(ingresoData)
            .then((result) => {
              console.log('✅ Recepción simplificada completada:', result)
              resolve(result)
            })
            .catch(err => {
              console.error('❌ Error en recepción simplificada:', err)
              reject(err)
            })
        } else {
          // Usar flujo tradicional (con asignación de almacenamiento)
          const traditionalData = {
            reference: ingresoData.reference,
            type: 'RECEPTION',
            status: 'Pendiente',
            supplier: ingresoData.supplier,
            notes: ingresoData.notes,
            details: ingresoData.items.map(item => ({
              product_id: item.product_id,
              quantity: item.quantity,
              lot_number: item.lot_number,
              expiry_date: item.expiry_date,
              production_date: item.production_date || null,
              pack_date: item.pack_date || null,
              best_before_date: item.best_before_date || null,
              serial_number: item.serial_number,
              destination_location_id: item.destination_location_id
            }))
          }
          
          this.createTransaction(traditionalData)
            .then(() => {
              resolve()
            })
            .catch(err => {
              reject(err)
            })
        }
      })
    },
    checkZoneCapacity() {
      if (!this.ingresoForm.destination_location_id) {
        this.selectedZone = null
        this.hasCapacity = true
        return
      }
      
      // Buscar la zona seleccionada
      const zoneId = this.ingresoForm.destination_location_id
      this.selectedZone = this.zones.find(z => z.id === zoneId)
      
      if (!this.selectedZone) {
        this.hasCapacity = true
        return
      }
      
      // Calcular la cantidad total de productos
      this.totalQuantity = this.ingresoForm.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
      
      // Verificar si hay capacidad disponible
      if (this.selectedZone) {
        const projectedUsage = this.selectedZone.current_usage + this.totalQuantity
        this.hasCapacity = projectedUsage <= this.selectedZone.capacity
      }
    },
    
    // Método para crear recepción simplificada
    createSimplifiedReception(ingresoData) {
      console.log('🚀 Iniciando creación simplificada:', ingresoData)
      return this.$store.dispatch('transactions/createSimplifiedReception', ingresoData)
    },
    handleCapacityStatus(status) {
      this.hasCapacity = status.hasCapacity
    },
    getStatusColor(status) {
      const colors = {
        'Completado': 'success',
        'Pendiente': 'warning',
        'Cancelado': 'error'
      }
      return colors[status] || 'secondary'
    },
    getStatusText(status) {
      const texts = {
        'Completado': 'Completado',
        'Pendiente': 'Pendiente',
        'Cancelado': 'Cancelado'
      }
      return texts[status] || status
    },
    submitIngreso() {
      if (!this.$refs.createForm.validate()) {
        return
      }

      this.submitting = true
      this.saveIngreso()
        .then(() => {
          this.showCreateModal = false
          this.resetIngresoForm()
          this.fetchIngresos()
          this.$store.dispatch('showSuccess', 'Ingreso creado correctamente')
        })
        .catch(err => {
          console.error('Error al crear ingreso:', err)
          let errorMessage = 'Error desconocido'
          
          if (err) {
            if (err.response && err.response.data && err.response.data.error) {
              errorMessage = err.response.data.error
            } else if (err.message) {
              errorMessage = err.message
            } else if (typeof err === 'string') {
              errorMessage = err
            }
          }
          
          this.$store.dispatch('showError', `Error al crear ingreso: ${errorMessage}`)
        })
        .finally(() => {
          this.submitting = false
        })
    },
    addItem() {
      this.newIngreso.items.push(blankIngresoItem())
    },
    removeItem(index) {
      this.newIngreso.items.splice(index, 1)
      this.checkZoneCapacity()
    },
    loadZones() {
      // Reset zone selection when warehouse changes
      this.newIngreso.zone_id = null
    },
    // Lector ZXing para la cámara. ASSUME_GS1 hace que un GS1-128 salga con el
    // identificador ]C1 y los FNC1 como GS (ASCII 29), igual que el lector físico
    // (DS2278); sin esa pista ZXing descarta los FNC1 y un código con dos campos
    // variables (lote + serie) no se puede separar.
    //
    // No usar TRY_HARDER: en @zxing/library 0.21 el modo "video" invierte 1 de
    // cada 2 cuadros (flag global FRAME_INDEX) y TRY_HARDER rota la imagen, lo
    // que vuelve a alternar el flag; el resultado es que TODOS los cuadros llegan
    // invertidos y nunca decodifica. Además un cuadro sin código tarda ~1 s.
    // Se desactiva el autoinvert (las etiquetas son negro sobre blanco) y se
    // limitan los formatos a los que usamos: un cuadro sin código tarda ~40 ms.
    createCameraReader() {
      const hints = new Map()
      hints.set(DecodeHintType.ASSUME_GS1, true)
      hints.set(DecodeHintType.POSSIBLE_FORMATS, [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.ITF,
        BarcodeFormat.RSS_14,
        BarcodeFormat.RSS_EXPANDED,
        BarcodeFormat.DATA_MATRIX,
        BarcodeFormat.QR_CODE
      ])
      const reader = new BrowserMultiFormatReader(hints)
      reader.createBinaryBitmap = function (mediaElement) {
        const isVideo = mediaElement instanceof HTMLVideoElement
        if (isVideo) {
          // Antes de que el video tenga dimensiones no hay nada que leer (y ZXing
          // dejaría cacheado un canvas de 0x0 para siempre)
          if (!mediaElement.videoWidth || !mediaElement.videoHeight) {
            throw new NotFoundException('Video sin cuadros todavía')
          }
          if (this.captureCanvas &&
              (this.captureCanvas.width !== mediaElement.videoWidth ||
               this.captureCanvas.height !== mediaElement.videoHeight)) {
            this.captureCanvas = undefined
            this.captureCanvasContext = undefined
          }
        }
        this.getCaptureCanvasContext(mediaElement)
        if (isVideo) {
          this.drawFrameOnCanvas(mediaElement)
        } else {
          this.drawImageOnCanvas(mediaElement)
        }
        const canvas = this.getCaptureCanvas(mediaElement)
        const luminanceSource = new HTMLCanvasElementLuminanceSource(canvas, false)
        return new BinaryBitmap(new HybridBinarizer(luminanceSource))
      }
      return reader
    },
    async startScan(itemIndex) {
      this.currentScanningIndex = itemIndex
      this.cameraScanBusy = false
      this.showBarcodeScanner = true
      
      try {
        this.codeReader = this.createCameraReader()
        
        // Verificar si hay cámaras disponibles (como en la versión que funcionaba)
        const videoInputDevices = await this.codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error('No se encontraron cámaras disponibles')
        }

        // Usar directamente decodeFromVideoDevice sin especificar dispositivo
        await this.codeReader.decodeFromVideoDevice(
          undefined, // Usar dispositivo por defecto
          this.$refs.videoElement,
          (result) => {
            // ZXing dispara el callback en cada cuadro: procesar un solo código a la vez
            if (result && !this.cameraScanBusy) {
              this.cameraScanBusy = true
              this.handleBarcodeScan(result.text)
            }
          }
        )
      } catch (error) {
        console.error('Error initializing barcode scanner:', error)
        
        // Manejar diferentes tipos de errores
        let errorMessage = 'Error al inicializar el escáner'
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara.'
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No se encontró ninguna cámara en el dispositivo.'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'El navegador no soporta acceso a la cámara.'
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'La cámara está siendo usada por otra aplicación.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
        this.showBarcodeScanner = false
      }
    },
    stopScanner(keepIndex = false) {
      try {
        // Detener el lector de códigos de barras
        if (this.codeReader) {
          this.codeReader.reset()
        }
        
        // Detener el stream de video
        if (this.$refs.videoElement && this.$refs.videoElement.srcObject) {
          const stream = this.$refs.videoElement.srcObject
          const tracks = stream.getTracks()
          tracks.forEach(track => track.stop())
          this.$refs.videoElement.srcObject = null
        }
      } catch (error) {
        console.error('Error stopping scanner:', error)
      }
      
      this.showBarcodeScanner = false
      this.cameraScanBusy = false
      if (!keepIndex) {
        this.currentScanningIndex = null
      }
    },
    // Cámara del card: exactamente el mismo flujo que el lector en "Escanear
    // múltiples" (análisis GS1, validación, búsqueda por GTIN/caja, datos
    // variables, consolidación, modal de creación), con destino el card desde el
    // que se abrió la cámara.
    async handleBarcodeScan(barcode) {
      const targetIndex = this.currentScanningIndex
      try {
        const handled = await this.processBarcodeValue(barcode, { source: 'camera', targetIndex })
        if (handled === 'create-product') {
          // Se abre el modal de creación: conservar el índice para asignar el
          // producto nuevo a ese card (addNewlyCreatedProductToIngreso)
          this.stopScanner(true)
        } else {
          // Agregado, informativo o error: se cierra la cámara. Si quedara
          // abierta, ZXing volvería a leer el mismo código cada 500 ms y el
          // mismo aviso se repetiría sin parar; el usuario reabre para reintentar
          this.stopScanner()
        }
      } catch (error) {
        console.error('Error procesando escaneo:', error)
        this.stopScanner()
        this.$store.dispatch('showError', 'Error al procesar el código escaneado')
      }
    },
    getFilteredProducts(index) {
      if (this.filteredProductsByIndex[index]) {
        return this.filteredProductsByIndex[index]
      }
      return this.products
    },
    handleProductSearch(search, index) {
      // Actualizar el texto de búsqueda
      this.newIngreso.items[index].productSearch = search
      
      if (!search || search.length < 2) {
        // Si la búsqueda es muy corta, usar la lista completa de productos
        delete this.filteredProductsByIndex[index]
        return
      }

      // Filtrar productos localmente por SKU o nombre
      const searchLower = search.toLowerCase()
      const filteredProducts = this.products.filter(product => 
        product.sku.toLowerCase().includes(searchLower) ||
        product.name.toLowerCase().includes(searchLower)
      )
      
      // Actualizar la lista de productos filtrados para este item
      this.filteredProductsByIndex[index] = filteredProducts
      
      // Si se encontró exactamente un producto y la búsqueda es específica, seleccionarlo automáticamente
      if (filteredProducts.length === 1 && search.length >= 4) {
        this.newIngreso.items[index].product_id = filteredProducts[0].id
      }
    },
    handleProductSelection(index, productId) {
      if (productId) {
        this.newIngreso.items[index].product_id = productId
        this.checkZoneCapacity()
      }
    },
    handleQuantityChange(index) {
      // Si la cantidad no es 1, limpiar el serial number
      if (this.newIngreso.items[index].quantity !== 1) {
        this.newIngreso.items[index].serial_number = ''
      }
    },
    handleQuantityChangeEdit(index) {
      // Si la cantidad no es 1, limpiar el serial number en edición
      if (this.editingIngreso.details[index].quantity !== 1) {
        this.editingIngreso.details[index].serial_number = ''
      }
    },
    getFilteredProductsEdit(index) {
      if (this.filteredProductsByIndexEdit[index]) {
        return this.filteredProductsByIndexEdit[index]
      }
      return this.products
    },
    handleProductSearchEdit(search, index) {
      // Actualizar el texto de búsqueda
      this.editingIngreso.details[index].productSearch = search
      
      if (!search || search.length < 2) {
        // Si la búsqueda es muy corta, usar la lista completa de productos
        delete this.filteredProductsByIndexEdit[index]
        return
      }

      // Filtrar productos localmente por SKU o nombre
      const searchLower = search.toLowerCase()
      const filteredProducts = this.products.filter(product => 
        product.sku.toLowerCase().includes(searchLower) ||
        product.name.toLowerCase().includes(searchLower)
      )
      
      // Actualizar la lista de productos filtrados para este item
      this.filteredProductsByIndexEdit[index] = filteredProducts
      
      // Si se encontró exactamente un producto y la búsqueda es específica, seleccionarlo automáticamente
      if (filteredProducts.length === 1 && search.length >= 4) {
        this.editingIngreso.details[index].product_id = filteredProducts[0].id
      }
    },
    handleProductSelectionEdit(index, productId) {
      if (productId) {
        this.editingIngreso.details[index].product_id = productId
      }
    },
    saveEditedIngreso() {
      if (!this.$refs.editForm.validate()) {
        return
      }

      this.submitting = true
      
      const updateData = {
        reference: this.editingIngreso.reference,
        supplier: this.editingIngreso.supplier,
        status: this.editingIngreso.status,
        notes: this.editingIngreso.notes,
        details: this.editingIngreso.details.map(detail => ({
          product_id: detail.product_id,
          quantity: detail.quantity,
          lot_number: detail.lot_number,
          expiry_date: detail.expiry_date,
          status: detail.status || 'pending'
        }))
      }

      console.log('=== DEBUG SAVE EDITED INGRESO ===')
      console.log('editingIngreso.details:', this.editingIngreso.details)
      console.log('updateData:', updateData)
      console.log('updateData.details:', updateData.details)

      // Usar la acción del store para actualizar la transacción
      this.updateTransaction({ id: this.editingIngreso.id, data: updateData })
        .then(() => {
          this.showEditModal = false
          this.fetchIngresos()
          this.$store.dispatch('showSuccess', 'Ingreso actualizado correctamente')
        })
        .catch(err => {
          console.error('Error al actualizar ingreso:', err)
          this.$store.dispatch('showError', 'Error al actualizar ingreso: ' + (err.message || 'Error desconocido'))
        })
        .finally(() => {
          this.submitting = false
        })
    },
    async createStorageList(item) {
      // Verificar que la transacción esté en estado pendiente
      if (item.status !== 'Pendiente') {
        this.$store.dispatch('showError', 'Solo se pueden crear listas de almacenamiento para ingresos pendientes')
        return
      }

      // El switch piece-by-piece conserva la última elección del usuario
      // (preferencia persistida); se muestra el modal de configuración
      this.selectedIngreso = item
      this.showStorageConfigDialog = true
    },
    // ===== Preferencias de UI persistidas por usuario =====
    async loadUserPreferences() {
      try {
        const { data } = await api.getUserPreferences()
        this.applyingUserPreferences = true
        if (typeof data?.ingreso_simplified_mode === 'boolean') {
          this.simplifiedMode = data.ingreso_simplified_mode
        }
        if (typeof data?.storage_piece_by_piece === 'boolean') {
          this.storageConfig.pieceByPieceEnabled = data.storage_piece_by_piece
        }
        // Los watchers corren en el siguiente tick: recién ahí se libera el flag
        await this.$nextTick()
      } catch (error) {
        console.warn('No se pudieron cargar las preferencias del usuario:', error?.message || error)
      } finally {
        this.applyingUserPreferences = false
      }
    },
    saveUserPreference(key, value) {
      // No re-guardar lo que acabamos de leer del backend
      if (this.applyingUserPreferences) return
      api.updateUserPreferences({ [key]: value }).catch(error => {
        console.warn(`No se pudo guardar la preferencia ${key}:`, error?.message || error)
      })
    },
    async confirmCreateStorageList() {
      if (!this.selectedIngreso) return

      this.submitting = true

      try {
        // Llamar al API para crear la lista de almacenamiento
        const response = await api.createStorageList({
          transaction_id: this.selectedIngreso.id,
          storage_strategy: 'abc_optimized', // Estrategia por defecto
          piece_by_piece_enabled: this.storageConfig.pieceByPieceEnabled
        })

        const storageList = response.data

        this.$store.dispatch('showSuccess', 
          `Lista de almacenamiento ${storageList.list_number} creada exitosamente`
        )

        // Cerrar modal
        this.showStorageConfigDialog = false
        this.selectedIngreso = null

        // Actualizar la lista de ingresos
        await this.fetchIngresos()

        // Preguntar si desea ir a la vista de asignación de servicios
        const goToAssignment = confirm(
          '¿Desea ir a la vista de Asignación de Servicios para gestionar la lista creada?'
        )

        if (goToAssignment) {
          this.$router.push({
            path: '/service-assignment',
            query: { tab: 'storage' }
          })
        }

      } catch (error) {
        console.error('Error creating storage list:', error)
        let errorMessage = 'Error al crear la lista de almacenamiento'
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
      } finally {
        this.submitting = false
      }
    },
    // Funciones para el modal de editar
    addEditItem() {
      this.editingIngreso.details.push({
        product_id: null,
        quantity: 1,
        lot_number: '',
        expiry_date: '',
        serial_number: '',
        productSearch: ''
      })
    },
    removeEditItem(index) {
      this.editingIngreso.details.splice(index, 1)
    },
    async startScanEdit(itemIndex) {
      this.currentScanningIndex = itemIndex
      this.showBarcodeScanner = true
      
      try {
        this.codeReader = this.createCameraReader()
        
        const videoInputDevices = await this.codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error('No se encontraron cámaras disponibles')
        }

        await this.codeReader.decodeFromVideoDevice(
          undefined,
          this.$refs.videoElement,
          (result) => {
            if (result) {
              this.handleBarcodeScanEdit(result.text)
            }
          }
        )
      } catch (error) {
        console.error('Error initializing barcode scanner:', error)
        
        let errorMessage = 'Error al inicializar el escáner'
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Permisos de cámara denegados. Por favor, permite el acceso a la cámara.'
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No se encontró ninguna cámara en el dispositivo.'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'El navegador no soporta acceso a la cámara.'
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'La cámara está siendo usada por otra aplicación.'
        } else if (error.message) {
          errorMessage = error.message
        }
        
        this.$store.dispatch('showError', errorMessage)
        this.showBarcodeScanner = false
      }
    },
    async handleBarcodeScanEdit(barcode) {
      try {
        // Mismo análisis que el lector: si es GS1 se busca por el GTIN (01)
        const analysis = this.analyzeBarcode(barcode)
        const product = await this.$store.dispatch('products/findProductByBarcode', analysis.gtin || barcode)
        if (product && this.currentScanningIndex !== null) {
          this.editingIngreso.details[this.currentScanningIndex].product_id = product.id
          this.stopScanner()
        } else {
          this.$store.dispatch('showError', 'Producto no encontrado')
        }
      } catch (error) {
        console.error('Error finding product:', error)
        this.$store.dispatch('showError', 'Error al buscar el producto')
      }
    },

    closeCreateModal() {
      // Resetear modo escaneo múltiple
      this.isMultipleScanMode = false
      this.multipleScanInput = ''
      this.scannedProducts = []
      this.lastScannedProduct = null
      this.scanStatus = { type: '', message: '' }
      
      // Cerrar modal
      this.showCreateModal = false
    },

    // Métodos para escaneo múltiple
    toggleMultipleScanMode() {
      if (this.isMultipleScanMode) {
        // Finalizar modo escaneo
        this.isMultipleScanMode = false
        this.multipleScanInput = ''
        this.scannedProducts = []
        this.lastScannedProduct = null
        this.scanStatus = { type: '', message: '' }
      } else {
        // Activar modo escaneo
        this.isMultipleScanMode = true
        this.multipleScanInput = ''
        this.scannedProducts = []
        this.lastScannedProduct = null
        this.scanStatus = { type: '', message: '' }
        
        // Enfocar el input después de que se muestre
        this.$nextTick(() => {
          if (this.$refs.multipleScanInput) {
            this.$refs.multipleScanInput.focus()
          }
        })
      }
    },

    handleBarcodeInput(value) {
      // Limpiar mensajes de estado después de un breve delay
      if (this.scanStatus.message) {
        setTimeout(() => {
          this.scanStatus = { type: '', message: '' }
        }, 2000)
      }
    },

    // ========== MÉTODOS DE ANÁLISIS DE CÓDIGOS DE BARRAS ==========
    
    // Detectar tipo de código de barras y extraer GTIN si es GS1
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      let cleaned = barcode.trim()

      // Prefijo AIM del lector (identificador de simbología, inequívoco):
      // GS1: ]C1 (GS1-128), ]d2 (GS1 DataMatrix), ]Q3 (GS1 QR), ]e0-]e3 (DataBar/compuestos).
      // NO GS1: ]E0 (EAN-13), ]E4 (EAN-8), ]A0 (Code 39), etc. — OJO: mayúscula/minúscula importa.
      let aimId = null
      let aimIsGS1 = false
      const aimMatch = cleaned.match(/^[\]+]([A-Za-z][0-9A-Za-z])/)
      if (aimMatch) {
        aimId = aimMatch[1]
        cleaned = cleaned.substring(3)
        aimIsGS1 = /^(C1|d2|Q3|e[0-3])$/.test(aimId)
      }
      // FNC1 en primera posición (ZXing lo entrega como GS en GS1 DataMatrix/QR):
      // solo indica "es GS1", no forma parte de los datos
      if (/^\u001D/.test(cleaned)) {
        cleaned = cleaned.replace(/^\u001D+/, '')
        aimIsGS1 = true
      }
      // Simbología detectada por AIM (para re-imprimir en la misma simbología)
      const AIM_TO_SYMBOLOGY = {
        C1: 'gs1-128', d2: 'gs1datamatrix', Q3: 'gs1qrcode',
        e0: 'databarexpandedstacked', e1: 'databarexpandedstacked', e2: 'databarexpandedstacked', e3: 'databarexpandedstacked',
        E0: 'ean13', E4: 'ean8'
      }
      const scannedSymbology = aimId ? (AIM_TO_SYMBOLOGY[aimId] || null) : null

      console.log('🧪 Analizando código:', {
        original: cleaned,
        length: cleaned.length,
        aimId: aimId,
        aimIsGS1: aimIsGS1,
        startsWithAI01: cleaned.startsWith('01'),
        hasParens: cleaned.includes('(')
      })

      // Detección GS1 mejorada. Si el lector envió un AIM ID no-GS1 (ej: ]E4 EAN-8),
      // se respeta esa clasificación y no se aplican heurísticas GS1.
      const isGS1 = aimIsGS1 ||
                   (!aimId && (
                     cleaned.includes('(') ||
                     (cleaned.startsWith('01') && cleaned.length > 16) ||
                     (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                     (cleaned.includes(String.fromCharCode(29))) || // Incluir códigos con separador FNC1
                     this.isPureAIStream(cleaned) // Códigos que comienzan con cualquier AI (ej: solo lote "10ABC123")
                   ))
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0

        // Validación de certificación GS1: numérico/alfanumérico, fechas reales,
        // dígito verificador de claves (checklist sección 1); si viene con
        // paréntesis, también valida largos exactos por AI (checklist 1.20)
        const validationErrors = this.validateGS1Scan(cleaned)
        if (validationErrors.length > 0) {
          console.warn('⛔ Errores de validación GS1:', validationErrors)
        }

        console.log('✅ Identificado como GS1:', {
          gtin: gtin,
          hasVariableData: hasVariableData,
          variableData: variableData
        })

        return {
          type: 'GS1',
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData,
          validationErrors: validationErrors,
          scannedSymbology: scannedSymbology
        }
      }
      
      // Solo evaluar códigos de longitud fija si NO es GS1
      // ITF-14 (exactamente 14 dígitos, GTIN-14 de unidad de despacho)
      if (/^\d{14}$/.test(cleaned)) {
        console.log('📊 Identificado como ITF-14:', cleaned)
        return { type: 'ITF-14', gtin: cleaned, original: cleaned, variableData: null }
      }

      // EAN-13 (exactamente 13 dígitos)
      if (/^\d{13}$/.test(cleaned)) {
        console.log('📊 Identificado como EAN-13:', cleaned)
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }

      // UPC-A (exactamente 12 dígitos)
      if (/^\d{12}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC-A:', cleaned)
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }

      // 8 dígitos: UPC-E o EAN-8
      if (/^\d{8}$/.test(cleaned)) {
        // Ambigüedad EAN-8 vs fecha GS1 sola (AI 11/13/15/17 + YYMMDD = 8 dígitos):
        // si además es una fecha plausible, se adjunta como fallback. El flujo busca
        // el producto primero (gana un EAN-8 registrado); si no existe, muestra la fecha.
        const gs1Fallback = this.strongDateAIData(cleaned)
        if (!isValidGS1Key(cleaned, [8])) {
          const upca = expandUPCE(cleaned)
          if (upca) {
            // El código NUNCA se altera: gtin = el UPC-E tal cual vino. La
            // expansión a UPC-A valida el verificador y sirve para matchear
            // (el store la resuelve al buscar), pero no se muestra ni guarda.
            console.log('📊 Identificado como UPC-E:', cleaned, '(expansión UPC-A interna:', upca + ')')
            return { type: 'UPC-E', gtin: cleaned, original: cleaned, upcaExpansion: upca, variableData: null, gs1Fallback }
          }
        }
        console.log('📊 Identificado como EAN-8:', cleaned)
        return { type: 'EAN-8', gtin: cleaned, original: cleaned, variableData: null, gs1Fallback }
      }
      
      // Códigos de longitud intermedia que podrían ser variantes
      if (/^\d{10,11}$/.test(cleaned)) {
        console.log('📊 Identificado como UPC:', cleaned)
        return { type: 'UPC', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      // Otro formato
      console.log('❓ Clasificado como Otro:', cleaned)
      return { type: 'Otro', gtin: cleaned, original: cleaned, variableData: null }
    },

    // Extraer GTIN y datos variables de código GS1
    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      let cleaned = gs1Code.replace(/[()]/g, '') // Remover paréntesis si existen
      cleaned = this.normalizeFNC1(cleaned)      // Normalizar FNC1 desde escáner DS2278
      let gtin = null
      let variableData = {}
      
      console.log('🔧 Extrayendo GTIN de:', cleaned)
      console.log('🔧 Código después de normalizar FNC1:', cleaned.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      // Log del código normalizado para debug
      if (window.debugGS1) {
        console.log('🚨 DEBUG GS1: Código detectado')
        console.log('Código completo:', cleaned)
        console.log('¿Contiene FNC1?', cleaned.includes(this.GS))
      }
      
      try {
        // Método mejorado según especificaciones del usuario:
        // "Si el código empieza con 01 los siguientes 14 caracteres son el GTIN"
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          // Extraer exactamente los siguientes 14 caracteres después de "01"
          gtin = cleaned.substring(2, 16) // Posiciones 2-15 (14 dígitos del GTIN)
          const remainingData = cleaned.substring(16) // Todo lo que viene después del GTIN
          
          console.log('✅ GTIN extraído según especificación (01 + 14 chars):', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            // Parsear datos variables que vienen después del GTIN
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          // Métodos alternativos para otros casos
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1] // GTIN de 14 dígitos
            const remainingData = gtinMatch[2] // Datos adicionales después del GTIN

            console.log('✅ GTIN extraído (regex):', gtin)
            console.log('🔍 Datos restantes para parsing:', remainingData)

            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          } else {
            // Código GS1 que NO comienza con (01): parsear todo como secuencia de AIs
            // (ej: solo lote "10ABC123", solo vencimiento, SSCC, etc.). Sin AI 01 no hay GTIN.
            const parsedData = this.parseGS1VariableData(cleaned)
            if (Object.keys(parsedData).length > 0) {
              variableData = parsedData
              console.log('✅ Secuencia de AIs sin GTIN parseada:', parsedData)
              // (02) GTIN contenido identifica el producto de la unidad logística
              // (checklist GS1 3.3: ej. (02)GTIN(10)LOTE(17)VENC(37)CANT)
              if (parsedData.content_gtin) {
                gtin = parsedData.content_gtin
                console.log('✅ GTIN contenido (02) usado como identificador de producto:', gtin)
              }
            } else if (/^\d+$/.test(cleaned) && cleaned.length > 14) {
              // Si es un código largo sin AI visible, intentar extraer los primeros 14 dígitos
              gtin = cleaned.substring(0, 14)
              console.log('⚠️ GTIN extraído por longitud:', gtin)
            }
          }
        }

        // Si no se pudo extraer GTIN ni parsear AIs, usar el código original
        if (!gtin && Object.keys(variableData).length === 0) {
          console.log('❌ No se pudo extraer GTIN, usando código completo')
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      console.log('🎯 Resultado final - GTIN:', gtin, 'Variables:', variableData)
      return { gtin, variableData }
    },

    // ========== PARSER GS1 PROFESIONAL ==========
    
    // Parsear pesos GS1 (310x-340x): el 4º dígito del AI indica los decimales
    parseGS1Weight(aiKey, value) {
      const decimals = parseInt(aiKey.charAt(3), 10)
      const num = parseInt(value, 10)
      if (isNaN(num)) return value
      return num / Math.pow(10, isNaN(decimals) ? 0 : decimals)
    },

    // Función de utilidad para parsear fechas GS1 (YYMMDD → YYYY-MM-DD)
    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        console.warn(`⚠️ Fecha GS1 inválida (${aiType}):`, dateValue)
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      // Validar rangos básicos
      const monthNum = parseInt(mm)
      const dayNum = parseInt(dd)
      
      if (monthNum < 1 || monthNum > 12) {
        console.warn(`⚠️ Mes inválido en fecha GS1 (${aiType}):`, dateValue, `→ mm=${mm}`)
        return null
      }
      
      // Corregir día 00 a 01 para fechas válidas
      if (dd === '00') {
        dd = '01'
        console.log(`📅 Corrigiendo día 00 → 01 en fecha ${aiType}: ${dateValue} → ${yy}${mm}${dd}`)
      }
      
      // Validar día después de corrección
      if (dayNum < 0 || dayNum > 31) {
        console.warn(`⚠️ Día inválido en fecha GS1 (${aiType}):`, dateValue, `→ dd=${dd}`)
        return null
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}`
      console.log(`📅 Fecha GS1 parseada (${aiType}): ${dateValue} → ${parsedDate}`)
      return parsedDate
    },
    
    // Export a Excel del reporte GS1 de entradas (checklist 4.1/4.3):
    // GTIN, lote, fechas, serie, SSCC, cantidad, origen y destino
    async exportGs1Excel() {
      try {
        const params = new URLSearchParams({ direction: 'in', format: 'xlsx' })
        if (this.filters.dateFrom) params.set('date_from', this.filters.dateFrom)
        if (this.filters.dateTo) params.set('date_to', this.filters.dateTo)
        const response = await api.downloadGs1MovementsXlsx(params.toString())
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.download = 'movimientos_gs1_entradas.xlsx'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$store.dispatch('showSuccess', 'Reporte GS1 de entradas exportado a Excel')
      } catch (e) {
        console.error('Error exportando Excel GS1:', e)
        this.$store.dispatch('showError', 'Error exportando el reporte GS1')
      }
    },

    // ===== Validador GS1 (checklist 5.2) =====
    openValidator() {
      this.validatorInput = ''
      this.validatorResult = null
      this.validatorDialog = true
    },
    // Valida UN código escaneado (con o sin prefijo AIM) → resultado parcial
    validateOneScan(raw) {
      // AIM Code ID del lector → simbología leída (checklist 3.2: un GS1-128
      // real llega como ]C1 porque lleva FNC1 en primera posición; un Code 128
      // plano llega como ]C0)
      const AIM_NAMES = {
        C1: 'GS1-128', C0: 'Code 128 (NO GS1)', d2: 'GS1 DataMatrix', Q3: 'GS1 QR',
        e0: 'GS1 DataBar', e1: 'GS1 DataBar', e2: 'GS1 DataBar', e3: 'GS1 DataBar',
        E0: 'EAN-13', E1: 'EAN-13', E2: 'EAN-13', E3: 'EAN-13', E4: 'EAN-8', A0: 'Code 39'
      }
      const aimMatch = /^[\]+](C[01]|e[0-3]|E[0-4]|d2|Q3|A0)/.exec(raw)
      // Quitar AIM pero CONSERVAR paréntesis: con ellos validateGS1Scan valida
      // además los largos exactos por AI (checklist 1.20)
      const noAim = raw.replace(/^[\]+](C[01]|e[0-3]|E[0-4]|d2|Q3|A0)/, '')
      const errors = this.validateGS1Scan(noAim)
      const normalized = this.normalizeFNC1(noAim.replace(/[()]/g, ''))
      const { items } = this.parseGS1(normalized)
      // Secuencia con separadores FNC1 visibles (checklist 3.4)
      const sequence = items.map(it => `(${it.ai})${it.value}${it.fnc1After ? '⟨FNC1·GS⟩' : ''}`).join('')
      return {
        errors,
        items,
        aim: aimMatch ? ']' + aimMatch[1] : null,
        symbology: aimMatch ? (AIM_NAMES[aimMatch[1]] || 'Desconocida') : null,
        hadFNC1: normalized.includes(this.GS),
        sequence
      }
    },
    runValidator() {
      const raw = String(this.validatorInput || '').trim()
      if (!raw) return
      // Varios escaneos CONCATENADOS en el campo (etiqueta con símbolos GS1-128
      // apilados, o lector sin sufijo Enter): cada prefijo AIM inicia un código
      // nuevo → se valida cada uno y se muestra el conjunto. Nota: un valor de
      // AI que contenga "+"+letra podría partirse de más (caso exótico).
      const segments = raw.split(/(?=[\]+][A-Za-z][0-9A-Za-z])/).filter(s => s.trim())
      const results = (segments.length > 1 ? segments : [raw]).map(s => this.validateOneScan(s.trim()))
      const n = results.length
      const first = results[0]
      this.validatorResult = {
        errors: n === 1 ? first.errors : results.flatMap((r, i) => r.errors.map(e => `código ${i + 1}: ${e}`)),
        items: results.flatMap(r => r.items),
        aim: first.aim,
        symbology: n === 1 ? first.symbology
          : `${[...new Set(results.map(r => r.symbology).filter(Boolean))].join(' + ') || 'Desconocida'} · ${n} códigos leídos`,
        hadFNC1: results.some(r => r.hadFNC1),
        sequence: results.map(r => r.sequence).filter(Boolean).join('  ‖  ')
      }
    },

    // Construir índices de búsqueda
    // Si un código de 8 dígitos es un AI de fecha (11/13/15/17) + fecha YYMMDD plausible,
    // devuelve sus datos parseados; si no, null. Se usa como fallback para el caso
    // ambiguo EAN-8 vs fecha GS1 sola cuando el dígito verificador EAN coincide por azar.
    strongDateAIData(code) {
      const m = /^(11|13|15|17)(\d{2})(\d{2})(\d{2})$/.exec(code)
      if (!m) return null
      const mes = Number(m[3])
      const dia = Number(m[4])
      // GS1 permite día 00 (= fin de mes)
      if (mes < 1 || mes > 12 || dia > 31) return null
      const data = this.parseGS1VariableData(code)
      return Object.keys(data).length > 0 ? data : null
    },

    // Validación GS1 de certificación (checklist sección 1): por cada AI del código
    // valida que el dato sea numérico donde corresponde, que las fechas AAMMDD sean
    // reales (mes 01-12, día válido para el mes; día 00 = fin de mes) y el dígito
    // verificador de las claves (GTIN, SSCC, GLN, GSIN, GSRN, GDTI).
    // Devuelve lista de errores legibles; vacía si el código es válido.
    validateGS1Scan(code) {
      const s = String(code || '')
      // Formato con paréntesis (validador / digitación manual): los límites de cada
      // AI son explícitos → validar largo exacto contra AI_DEFS (checklist 1.20:
      // AIs 90-99 hasta 30 caracteres; fijos con su largo exacto)
      const parenErrors = []
      if (s.includes('(')) {
        const aiIndex = this.buildAIIndex()
        const re = /\((\d{2,4})\)([^(]*)/g
        let m
        while ((m = re.exec(s)) !== null) {
          const found = this.findAI(m[1] + m[2], 0, aiIndex)
          if (!found || found.key !== m[1]) {
            parenErrors.push(`(${m[1]}): AI desconocido`)
            continue
          }
          const def = found.def
          const value = m[2]
          if (def.fixed) {
            if (value.length !== def.len) {
              parenErrors.push(`(${m[1]}) ${def.name}: debe tener exactamente ${def.len} caracteres (tiene ${value.length})`)
            }
          } else {
            const max = def.max ?? 30
            if (value.length > max) {
              parenErrors.push(`(${m[1]}) ${def.name}: ${value.length} caracteres supera el máximo de ${max}`)
            }
          }
        }
      }
      const result = this.parseGS1(s.replace(/[()]/g, ''))
      const errors = [...new Set([...parenErrors, ...result.warnings.filter(w => !w.startsWith('Empty'))])]
      const DATE_AIS = { '11': 'F. Producción', '13': 'F. Envasado', '15': 'F. Cons. Preferente', '17': 'F. Vencimiento' }
      const KEY_AIS = { '00': ['SSCC', 18], '01': ['GTIN', 14], '02': ['GTIN contenido', 14], '414': ['GLN', 13], '402': ['GSIN', 17], '8018': ['GSRN', 18] }

      for (const item of result.items) {
        const label = `(${item.ai})`
        if (DATE_AIS[item.aiBase]) {
          const name = DATE_AIS[item.aiBase]
          if (!/^\d{6}$/.test(item.value)) {
            errors.push(`${label} ${name}: "${item.value}" no es numérico de 6 dígitos (AAMMDD)`)
          } else {
            const mes = Number(item.value.slice(2, 4))
            const dia = Number(item.value.slice(4, 6))
            if (mes < 1 || mes > 12) {
              errors.push(`${label} ${name}: mes "${item.value.slice(2, 4)}" inválido`)
            } else if (dia > 0) {
              // día 00 es válido (= fin de mes, según GS1)
              const maxDia = new Date(2000 + Number(item.value.slice(0, 2)), mes, 0).getDate()
              if (dia > maxDia) {
                errors.push(`${label} ${name}: día "${item.value.slice(4, 6)}" inválido para el mes ${item.value.slice(2, 4)}`)
              }
            }
          }
        } else if (KEY_AIS[item.aiBase]) {
          const [name, len] = KEY_AIS[item.aiBase]
          if (!new RegExp(`^\\d{${len}}$`).test(item.value)) {
            errors.push(`${label} ${name}: debe tener ${len} dígitos numéricos`)
          } else if (!isValidGS1Key(item.value, [len])) {
            errors.push(`${label} ${name}: dígito verificador incorrecto (debería ser ${gs1CheckDigit(item.value.slice(0, -1))})`)
          }
        } else if (item.aiBase === '253') {
          if (!/^\d{13}/.test(item.value)) {
            errors.push(`${label} GDTI: debe comenzar con 13 dígitos numéricos`)
          } else if (!isValidGS1Key(item.value.slice(0, 13), [13])) {
            errors.push(`${label} GDTI: dígito verificador incorrecto`)
          }
        } else if (['310', '320', '330', '340'].includes(item.aiBase)) {
          if (!/^\d{6}$/.test(item.value)) {
            errors.push(`${label} ${item.name}: peso debe ser numérico de 6 dígitos`)
          }
        } else if (['30', '37'].includes(item.aiBase)) {
          if (!/^\d{1,8}$/.test(item.value)) {
            errors.push(`${label} ${item.name}: cantidad debe ser numérica (hasta 8 dígitos)`)
          }
        }
      }
      return errors
    },

    // Detecta códigos GS1 que comienzan con cualquier AI (ej: solo lote "10ABC123",
    // solo vencimiento "17270918"), sin exigir que traigan GTIN (01).
    // Desempate para numéricos puros con largo de GTIN (8/12/13/14): si el dígito
    // verificador GS1 es válido, es un EAN/UPC/ITF real y NO se trata como AIs;
    // si el checksum falla (ej: "17270918" → verificador sería 5), se intenta como AIs.
    isPureAIStream(code) {
      if (!code) return false
      if (/^\d+$/.test(code) && [8, 12, 13, 14].includes(code.length) && isValidGS1Key(code)) {
        return false
      }
      try {
        const { items, warnings } = this.parseGS1(code)
        return items.length > 0 && warnings.length === 0
      } catch (e) {
        return false
      }
    },

    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },
    
    // Encontrar AI en posición específica
    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      // Wildcards primero (310x, 320x, 390x, etc)
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      // Exactos 4 → 3 → 2 dígitos
      if (aiIndex.exact.has(chunk4)) return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      if (aiIndex.exact.has(chunk3)) return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      if (aiIndex.exact.has(chunk2)) return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      
      return null
    },
    
    // Validar si una secuencia AI es realmente válida - Simplificado con regla de AI único
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      console.log(`🔍 Validando AI ${aiMatch.key} con datos: "${remainingData.substring(0, 15)}..."`)
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          console.log(`❌ AI ${aiMatch.key} requiere ${requiredLength} chars, solo quedan ${remainingData.length}`)
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          console.log(`❌ AI fecha ${aiMatch.key} no tiene formato válido:`, remainingData.substring(0, 6))
          return false
        }
        
        console.log(`✅ AI fijo ${aiMatch.key} válido`)
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        console.log(`❌ AI variable ${aiMatch.key} sin contenido`)
        return false
      }
      
      // Validación especial para AI (01) GTIN
      if (aiMatch.base === '01') {
        if (remainingData.length < 14 || !/^\d{14}/.test(remainingData)) {
          console.log(`❌ AI (01) debe tener 14 dígitos, encontrado:`, remainingData.substring(0, 14))
          return false
        }
        
        const afterGTIN = remainingData.substring(14)
        if (afterGTIN.length > 0) {
          const nextPossibleAI = afterGTIN.substring(0, 2)
          const validNextAIs = ['10', '11', '13', '15', '17', '21', '30', '37', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '240', '241', '242', '243', '253', '310', '320', '330', '340', '390', '414', '402']
          
          if (!validNextAIs.includes(nextPossibleAI) && afterGTIN.length > 2) {
            console.log(`❌ AI (01) seguido de secuencia inválida: "${nextPossibleAI}"`)
            return false
          }
        }
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          console.log(`❌ AI (10) con contenido muy corto: "${remainingData}"`)
          return false
        }
      }
      
      console.log(`✅ AI variable ${aiMatch.key} válido`)
      return true
    },
    
    // Normalizar distintos formatos de FNC1 a un único \x1D
    normalizeFNC1(s) {
      if (!s) return s;
      
      if (window.debugGS1) {
        console.log('🔧 normalizeFNC1 input:', s);
      }
      
      let result = s
        .replace(/\u001D/g, this.GS)                   // FNC1 real (ASCII 29)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS);         // si usaste texto visible
      
      // Parche mejorado: el lector está enviando "0029" en lugar del FNC1 (GS).
      // Convertir SOLO cuando aparece como separador entre un valor y el siguiente AI válido.
      // El valor previo puede ser ALFANUMÉRICO (ej: lote "JGFD55"), no solo dígitos.
      const before = result;
      result = result.replace(/([A-Za-z0-9]{1,30}?)0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37|90|91|92|93|94|95|96|97|98|99|253|310|320|330|340|390|414|402))/g, `$1${this.GS}`);
      
      if (before !== result) {
        console.log('✅ Se aplicaron reemplazos de 0029:', {
          before: before,
          after: result.replace(new RegExp(this.GS, 'g'), '<FNC1>')
        });
      } else {
        console.log('❌ NO se aplicaron reemplazos de 0029');
        
        // Intentar método alternativo: buscar patrones específicos
        if (s.includes('0029')) {
          console.log('🔧 Intentando método alternativo para 0029...');
          // Buscar patrones más específicos
          const alternativePattern = /([A-Za-z0-9]{1,30}?)0029(\d{2,4})/g;
          let match;
          while ((match = alternativePattern.exec(s)) !== null) {
            const [full, beforeValue, afterValue] = match;
            console.log('📍 Patrón encontrado:', {
              full: full,
              before: beforeValue,
              after: afterValue,
              afterLength: afterValue.length
            });
            
            // Verificar si el "after" parece un AI válido
            const commonAIs = ['10', '11', '13', '15', '17', '21', '30', '37'];
            const possibleAI = afterValue.substring(0, 2);
            if (commonAIs.includes(possibleAI)) {
              console.log('✅ Detectado AI válido:', possibleAI);
              result = result.replace(full, `${beforeValue}${this.GS}${afterValue}`);
            }
          }
        }
      }
      
      console.log('🎯 normalizeFNC1 result:', result.replace(new RegExp(this.GS, 'g'), '<FNC1>'));
      return result;
    },

        // Parser GS1 completo con regla: cada AI aparece UNA SOLA VEZ
    parseGS1(raw) {
      if (!raw) return { items: [], warnings: ["Empty input"] }
      
      // Normalización mejorada para DS2278
      let s = String(raw).trim()
      s = s.replace(/^[\]+](C1|e0|E0|d2|Q3)/i, "")   // quitar prefijos de simbología
      s = this.normalizeFNC1(s)                   // normalizar FNC1 desde escáner DS2278
      s = s.replace(/<FNC1>|<GS>/gi, this.GS)    // algunos lectores usan textos literales
      
      console.log('🔍 Parseando GS1 completo:', s.replace(new RegExp(this.GS, 'g'), '<FNC1>'))
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set() // Registro de AIs ya procesados
      let i = 0
      
      while (i < s.length) {
        // Saltar separadores FNC1
        if (s[i] === this.GS) { 
          i++
          console.log('⚡ FNC1 encontrado y saltado')
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}: "${s.slice(i, i+4)}..."`)
          console.log(`⚠️ AI desconocido en posición ${i}:`, s.slice(i, i+4))
          break
        }
        
        const { key, base, def } = m
        i += key.length
        
        // REGLA FUNDAMENTAL: Verificar si este AI ya fue procesado
        if (processedAIs.has(base)) {
          console.log(`⚠️ AI ${base} duplicado detectado - IGNORANDO (regla: un AI por código)`)
          warnings.push(`AI duplicado ${base} ignorado`)
          break // O continuar hasta el final del string como contenido del AI anterior
        }
        
        console.log(`📊 Procesando AI ${key} (${def.name}) - Primer encuentro ✓`)
        processedAIs.add(base) // Marcar como procesado
        
        let value = ""
        let ambiguous = false
        let fnc1After = false

        if (def.fixed) {
          // Longitud fija
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            warnings.push(`Valor incompleto para (${key}) esperaba ${need} chars.`)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
                 } else {
          // Longitud variable: SOLO cortar con FNC1 real o límite máximo
           const max = def.max ?? 30
           let j = i
           let cutByFNC1 = false
           
           while (j < s.length && (j - i) < max) {
            // REGLA ESTRICTA: Solo cortar con FNC1 real
             if (s[j] === this.GS) { 
              console.log(`✂️ FNC1 encontrado, cortando en posición ${j}`)
               cutByFNC1 = true
               break 
             }
            j++
          }
          
          // Si no hay FNC1, tomar todo hasta el límite máximo o fin del string
          console.log(`📏 Campo variable sin FNC1: tomando hasta posición ${j} (${j-i} chars)`)
                 
          // OPCIONAL: Buscar AI solo si llegamos al límite máximo sin FNC1
          let cutByAI = false
          if ((j - i) >= max && j < s.length) {
            // Solo en este caso, buscar AI como posible separador
            for (let k = Math.max(i + 3, j - 10); k <= j + 2 && k < s.length - 1; k++) {
              const next = this.findAI(s, k, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(k + next.key.length)

                console.log(`🔍 AI potencial en límite máximo "${next.key}" en posición ${k}`)

                   if (this.isValidAISequence(next, remainingAfterAI)) {
                  console.log(`✂️ CORTANDO por límite máximo + AI válido: "${next.key}"`)
                  j = k
                  cutByAI = true
                     break
                   }
                 }
               }
           }

           // Checklist 1.20: valor variable que llega al máximo y sigue sin FNC1
           // ni un AI válido a continuación → el dato excede su largo permitido
           if ((j - i) >= max && j < s.length && !cutByAI && s[j] !== this.GS) {
             warnings.push(`(${key}): el dato supera el máximo de ${max} caracteres`)
           }

           value = s.slice(i, j)
           i = j
           if (s[i] === this.GS) { i++; fnc1After = true } // Consumir FNC1 si lo había
           if (cutByFNC1) fnc1After = true

          // Detectar ambigüedad solo si no fue cortado por FNC1
           if (!cutByFNC1) {
             const nxt = this.findAI(s, i, aiIndex)
            if (nxt && nxt.def && !nxt.def.fixed && !processedAIs.has(nxt.base)) {
              ambiguous = true
            }
           }
         }

        console.log(`✅ ${def.name} extraído: "${value}"${ambiguous ? ' (ambiguous)' : ''}`)
        // fnc1After: hubo separador FNC1 tras este AI variable (checklist 3.4: se
        // gestiona internamente y el desglose lo muestra como ⟨FNC1·GS⟩)
        items.push({ ai: key, aiBase: base, name: def.name, value, ambiguous, variable: !def.fixed, fnc1After })
      }
      
      console.log(`🎯 AIs procesados: ${Array.from(processedAIs).join(', ')}`)
      return { items, warnings }
    },
    
    // Parsear datos variables del GS1 - versión simplificada que usa el parser completo
    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      console.log('🔍 Parseando datos GS1 con parser profesional:', remainingData)
      
      try {
        // Usar el parser completo
        const result = this.parseGS1(remainingData)
        
        if (result.warnings.length > 0) {
          console.warn('⚠️ Advertencias GS1:', result.warnings)
        }
        
        // Variables temporales para fechas
        let expiryDate = null
        let productionDate = null
        let packagingDate = null
        let bestBeforeDate = null
        const otherAIs = [] // AIs válidos sin campo propio: se conservan para mostrarlos

        // Convertir items a formato esperado por el resto del código
        for (const item of result.items) {
          switch (item.aiBase) {
            case '02':
              // GTIN contenido en la unidad logística; viene acompañado de (37) cantidad
              variableData.content_gtin = item.value
              break
            case '00':
              variableData.sscc = item.value
              break
            case '253':
              variableData.gdti = item.value
              break
            case '414':
              variableData.gln = item.value
              break
            case '310':
              variableData.net_weight_kg = this.parseGS1Weight(item.ai, item.value)
              break
            case '320':
              variableData.net_weight_lb = this.parseGS1Weight(item.ai, item.value)
              break
            case '330':
              variableData.gross_weight_kg = this.parseGS1Weight(item.ai, item.value)
              break
            case '340':
              variableData.gross_weight_lb = this.parseGS1Weight(item.ai, item.value)
              break
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              // Fecha de producción YYMMDD → YYYY-MM-DD
              productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '13':
              // Fecha de empaquetado YYMMDD → YYYY-MM-DD
              packagingDate = this.parseGS1Date(item.value, 'empaquetado')
              if (packagingDate) {
                variableData.packaging_date = packagingDate
              }
              break
            case '15':
              // Fecha de consumo preferente YYMMDD → YYYY-MM-DD
              bestBeforeDate = this.parseGS1Date(item.value, 'consumo preferente')
              if (bestBeforeDate) {
                variableData.best_before_date = bestBeforeDate
              }
              break
            case '17':
              // Fecha de vencimiento YYMMDD → YYYY-MM-DD
              expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
            case '37':
              variableData.count = item.value
              break
            case '401':
              variableData.ginc = item.value
              break
            case '402':
              variableData.gsin = item.value
              break
            case '8003':
              variableData.grai = item.value
              break
            case '8004':
              variableData.giai = item.value
              break
            case '8018':
              variableData.gsrn = item.value
              break
            default:
              if (/^9\d$/.test(item.aiBase)) {
                // AIs de uso interno 90-99
                variableData[`custom_field_${item.aiBase}`] = item.value
              } else {
                otherAIs.push(`(${item.ai}) ${item.name}: ${item.value}`)
              }
              break
          }
        }

        if (otherAIs.length > 0) {
          variableData.other_ais = otherAIs
        }
        
        // Fecha de vencimiento: solo AI 17 (vencimiento) o, en su defecto, AI 15
        // (consumo preferente) — ambas indican "hasta cuándo sirve". La fecha de
        // FABRICACIÓN (11) y la de EMPAQUE (13) NUNCA se usan como vencimiento;
        // se guardan por separado (production_date / packaging_date).
        if (!expiryDate && bestBeforeDate) {
          variableData.expiry_date = bestBeforeDate
          console.log('📅 Sin AI 17; se usa consumo preferente (15) como vencimiento:', bestBeforeDate)
        }
        // production_date / packaging_date / best_before_date quedan en variableData
        // tal cual fueron extraídas (no se mezclan con expiry_date).
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      console.log('🎯 Datos variables extraídos:', variableData)
      return variableData
    },

    // ========== FUNCIÓN DE PRUEBA TEMPORAL ==========
    
    testGS1Parsing() {
      console.log('🧪 === INICIO PRUEBAS GS1 ===')
      
      // Códigos de prueba según especificaciones del usuario
      const testCodes = [
        // Con fecha de vencimiento (17)
        '01012345678901231722101210ABC123',
        // Con fecha de producción (11)  
        '01012345678901231122101021ABC123',
        // Con fecha de empaquetado (13)
        '01012345678901231322101321ABC123',
        // Con fecha de consumo preferente (15)
        '01012345678901231522101521ABC123',
              // Con día 00 (caso específico del usuario) - debe convertirse a 01
      '0101234567890123172812001012345',
      // Casos específicos de lotes problemáticos del usuario
      '05030267047666172611001012620', // Lote: 1212620 (funciona)
      '050302670476661727010010122154', // Lote: 1220154 (problema con 01)
      '050302670476802806001032312014', // Lote: 32312014 (problema con 014)
        // Con lote y serial
        '0101234567890123102022101221ABC12321SN123456',
        // Con separadores FNC1
        `01012345678901231722101210${String.fromCharCode(29)}21ABC123`,
        // Código simple EAN-13
        '7891234567890'
      ]
      
      testCodes.forEach((code, index) => {
        console.log(`\n🔍 Prueba ${index + 1}: ${code}`)
        const analysis = this.analyzeBarcode(code)
        console.log('Resultado:', analysis)
      })
      
      console.log('🧪 === FIN PRUEBAS GS1 ===\n')
      
      // Probar casos específicos de lotes problemáticos (solo si está habilitado)
      if (window.enableLotTests) {
        this.testLotParsing()
      }
      
      // Probar consolidación de productos idénticos
      this.testProductConsolidation()
    },
    
    // Método de prueba para consolidación de productos (solo desarrollo)
    testProductConsolidation() {
      console.log('🧪 === INICIO PRUEBAS CONSOLIDACIÓN ===')
      
      // Simular producto de prueba
      const mockProduct = {
        id: 999,
        name: 'Producto Test GS1',
        sku: 'TEST-GS1-001'
      }
      
      // Código GS1 idéntico (mismo GTIN, lote y fecha)
      const identicalCode = '01012345678901231722101210ABC123'
      
      console.log('📋 Estado inicial items:', this.newIngreso.items.length)
      
      // Simular escaneo del mismo código 3 veces
      for (let i = 1; i <= 3; i++) {
        console.log(`\n📱 Escaneo ${i}: ${identicalCode}`)
        const analysis = this.analyzeBarcode(identicalCode)
        console.log('Analysis:', analysis)
        
        this.addToMainProductListWithVariableData(mockProduct, analysis)
        
        console.log(`📋 Items después del escaneo ${i}:`, this.newIngreso.items.length)
        this.newIngreso.items.forEach((item, idx) => {
          if (item.product_id === mockProduct.id) {
            console.log(`   Item ${idx}: Cantidad=${item.quantity}, Lote=${item.lot_number}, Venc=${item.expiry_date}`)
          }
        })
      }
      
      // Verificar resultado
      const testItems = this.newIngreso.items.filter(item => item.product_id === mockProduct.id)
      if (testItems.length === 1 && testItems[0].quantity === 3) {
        console.log('✅ CONSOLIDACIÓN EXITOSA: 1 item con cantidad 3')
      } else {
        console.log('❌ CONSOLIDACIÓN FALLIDA:', testItems)
      }
      
      // Limpiar items de prueba
      this.newIngreso.items = this.newIngreso.items.filter(item => item.product_id !== mockProduct.id)
      
      console.log('🧪 === FIN PRUEBAS CONSOLIDACIÓN ===\n')
    },
    
    // ========== MÉTODOS PARA CREACIÓN DE PRODUCTOS ON-THE-FLY ==========
    
    // Cancelar creación de producto
    cancelCreateProduct() {
      this.showCreateProductConfirm = false
      this.currentScanningIndex = null
      this.lastScannedCode = ''
      this.lastExtractedGTIN = ''
      this.lastBarcodeAnalysis = null
      this.setScanStatus('ready', 'Escaneo cancelado. Listo para escanear.')
    },
    
    // Confirmar creación de producto
    confirmCreateProduct() {
      this.showCreateProductConfirm = false
      
      // Preparar datos para el nuevo producto
      this.prepareNewProductData()
      
      // Mostrar modal de creación
      this.showNewProductDialog = true
    },
    
    // Preparar datos del nuevo producto basados en el código escaneado
    prepareNewProductData() {
      const gtin = this.lastExtractedGTIN || this.lastScannedCode
      
      // El SKU por defecto ES el GTIN: la clave GS1 como identificación primaria
      // (los códigos internos tipo AUTOxxxx son la raíz de los quiebres de trazabilidad)
      const suggestedSKU = gtin
      
      // Preparar datos del producto
      this.newProductData = {
        sku: suggestedSKU,
        barcode: gtin, // Usar el GTIN extraído, no el código completo
        codigo_fabrica: '',
        name: '',
        description: '',
        category: '',
        abc_category: 'B', // Categoría por defecto
        warehouse_id: null,
        zone_id: null,
        location_id: null,
        current_stock: 0,
        min_stock: 0,
        max_stock: 0,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        status: 'Activo'
      }
      
      console.log('📋 Producto preparado para creación:', {
        sku: this.newProductData.sku,
        barcode: this.newProductData.barcode,
        originalCode: this.lastScannedCode,
        analysis: this.lastBarcodeAnalysis?.type
      })
    },
    
    // Guardar nuevo producto y agregarlo al ingreso
    async saveNewProduct(productData) {
      try {
        this.setScanStatus('loading', 'Creando producto...')
        
        // Crear el producto usando el store
        const newProduct = await this.$store.dispatch('products/createProduct', productData)
        
        this.showNewProductDialog = false
        
        // Actualizar lista de productos
        await this.$store.dispatch('products/fetchProducts')
        
        this.setScanStatus('success', `Producto "${newProduct.name}" creado exitosamente`)
        
        // Agregar el producto recién creado al ingreso automáticamente
        setTimeout(() => {
          this.addNewlyCreatedProductToIngreso(newProduct, this.lastBarcodeAnalysis)
        }, 500)
        
      } catch (error) {
        console.error('Error al crear producto:', error)
        this.setScanStatus('error', 'Error al crear el producto: ' + (error.message || 'Error desconocido'))
        this.showNewProductDialog = false
      }
    },
    
    // Agregar producto recién creado al ingreso
    addNewlyCreatedProductToIngreso(product, barcodeAnalysis) {
      console.log('➕ Agregando producto recién creado al ingreso:', {
        product: product.name,
        id: product.id,
        hasVariableData: barcodeAnalysis?.hasVariableData,
        scanningIndex: this.currentScanningIndex
      })
      
      try {
        // Verificar si viene de escaneo simple (tiene currentScanningIndex) o múltiple
        if (this.currentScanningIndex !== null) {
          // Escaneo simple: asignar al índice específico
          this.newIngreso.items[this.currentScanningIndex].product_id = product.id
          
          // Si tiene datos variables, también llenar los campos correspondientes
          if (barcodeAnalysis?.hasVariableData && barcodeAnalysis.variableData) {
            const vd = barcodeAnalysis.variableData
            
            // Llenar los campos de datos variables en el item actual
            if (vd.lot_number) {
              this.newIngreso.items[this.currentScanningIndex].lot_number = vd.lot_number
            }
            if (vd.serial_number) {
              this.newIngreso.items[this.currentScanningIndex].serial_number = vd.serial_number
            }
            if (vd.expiry_date) {
              this.newIngreso.items[this.currentScanningIndex].expiry_date = vd.expiry_date
            }
            // Fecha de fabricación (AI 11) se guarda por separado, no como vencimiento
            if (vd.production_date) {
              this.newIngreso.items[this.currentScanningIndex].production_date = vd.production_date
            }
            if (vd.packaging_date) {
              this.newIngreso.items[this.currentScanningIndex].packaging_date = vd.packaging_date
            }
            if (vd.best_before_date) {
              this.newIngreso.items[this.currentScanningIndex].best_before_date = vd.best_before_date
            }
          }
          
          // Limpiar currentScanningIndex
          this.currentScanningIndex = null
          
          this.$store.dispatch('showSuccess', `Producto creado y asignado: ${product.name}`)
          
        } else {
          // Escaneo múltiple: agregar a la lista de productos
          if (barcodeAnalysis?.hasVariableData) {
            // Producto con datos variables (GS1)
            this.addToMainProductListWithVariableData(product, barcodeAnalysis)
          } else {
            // Producto simple
            this.addToMainProductList(product)
          }
          
          this.setScanStatus('success', `Producto creado y agregado: ${product.name}`)
          
          // Mantener el foco en el input para continuar escaneando
          this.$nextTick(() => {
            if (this.$refs.multipleScanInput) {
              this.$refs.multipleScanInput.focus()
            }
          })
        }
        
        // Limpiar variables temporales
        this.lastScannedCode = ''
        this.lastExtractedGTIN = ''
        this.lastBarcodeAnalysis = null
        
      } catch (error) {
        console.error('Error agregando producto al ingreso:', error)
        if (this.currentScanningIndex !== null) {
          this.$store.dispatch('showError', 'Error al asignar producto')
        } else {
          this.setScanStatus('error', 'Error al agregar producto al ingreso')
        }
      }
    },

    // Método de prueba específico para parsing de lotes (casos problemáticos)
    testLotParsing() {
      console.log('🧪 === INICIO PRUEBAS LOTES ===')
      
      const lotTestCases = [
        {
          name: 'Lote que funciona correctamente',
          code: '05030267047666172611001012620', // Sin AI 01 inicial para probar solo datos variables
          expected: {
            gtin: '05030267047666',
            expiry_date: '2026-11-00', // Se corregirá día 00 → 01
            lot_number: '1212620' // Completo
          }
        },
        {
          name: 'Lote con 01 interno (problema)',
          code: '050302670476661727010010122154',
          expected: {
            gtin: '05030267047666', 
            expiry_date: '2027-01-00', // Se corregirá día 00 → 01
            lot_number: '1220154' // Debe ser completo, no cortado en "122"
          }
        },
        {
          name: 'Lote con 014 interno (problema)',
          code: '050302670476802806001032312014',
          expected: {
            gtin: '05030267047680',
            expiry_date: '2028-06-00', // Se corregirá día 00 → 01  
            lot_number: '32312014' // Debe ser completo, no cortado en "32312"
          }
        },
        {
          name: 'Código completo con AI 01',
          code: '01050302670476661727010010122154',
          expected: {
            gtin: '05030267047666',
            expiry_date: '2027-01-01', // Corregido
            lot_number: '1220154' // Completo
          }
        }
      ]
      
      lotTestCases.forEach((testCase, index) => {
        console.log(`\n🔍 Prueba Lote ${index + 1}: ${testCase.name}`)
        console.log(`   Código: ${testCase.code}`)
        
        try {
          const result = this.analyzeBarcode(testCase.code)
          console.log('   Resultado:', result)
          
          // Verificar GTIN
          const actualGTIN = result.gtin
          if (actualGTIN === testCase.expected.gtin) {
            console.log(`   ✅ GTIN correcto: ${actualGTIN}`)
          } else {
            console.log(`   ❌ GTIN incorrecto. Esperado: ${testCase.expected.gtin}, Obtenido: ${actualGTIN}`)
          }
          
          // Verificar datos variables si existen
          if (result.variableData) {
            const vd = result.variableData
            
            // Verificar fecha de vencimiento
            if (vd.expiry_date) {
              if (vd.expiry_date === testCase.expected.expiry_date) {
                console.log(`   ✅ Fecha vencimiento correcta: ${vd.expiry_date}`)
              } else {
                console.log(`   ⚠️ Fecha vencimiento. Esperado: ${testCase.expected.expiry_date}, Obtenido: ${vd.expiry_date}`)
              }
            }
            
            // Verificar lote (MUY IMPORTANTE)
            if (vd.lot_number === testCase.expected.lot_number) {
              console.log(`   ✅ LOTE CORRECTO: "${vd.lot_number}"`)
            } else {
              console.log(`   ❌ LOTE INCORRECTO. Esperado: "${testCase.expected.lot_number}", Obtenido: "${vd.lot_number}"`)
              console.log(`   📏 Longitud esperada: ${testCase.expected.lot_number.length}, Obtenida: ${vd.lot_number?.length || 0}`)
            }
          } else {
            console.log('   ❌ No se extrajeron datos variables')
          }
        } catch (error) {
          console.log(`   ❌ Error en prueba: ${error.message}`)
        }
      })
      
      console.log('\n🧪 === FIN PRUEBAS LOTES ===\n')
      console.log('💡 Si algún lote muestra como INCORRECTO, revisar la lógica del parser')
    },

    async processScannedBarcode() {
      await this.processBarcodeValue(this.multipleScanInput, { source: 'reader' })
    },

    // Mensaje de estado del escaneo: en el panel de escaneo múltiple y, si viene
    // de la cámara (el panel puede no estar visible), también como snackbar.
    notifyScan(source, type, message, duration = 3000) {
      this.setScanStatus(type, message, duration)
      if (source === 'camera' && type !== 'loading') {
        this.$store.dispatch(type === 'error' ? 'showError' : 'showSuccess', message)
      }
    },

    // Pipeline ÚNICO de procesamiento de un código (lector físico o cámara).
    // Devuelve: 'added' (producto agregado/consolidado), 'create-product' (se
    // abrió el modal de creación), 'info' (código informativo, nada que agregar)
    // o 'error' (código inválido / vacío).
    async processBarcodeValue(rawInput, { source = 'reader', targetIndex = null } = {}) {
      const rawBarcode = (rawInput || '').trim()
      const isReader = source === 'reader'
      const resetReaderInput = () => {
        if (!isReader) return
        this.multipleScanInput = ''
        this.$nextTick(() => {
          if (this.$refs.multipleScanInput) {
            this.$refs.multipleScanInput.focus()
          }
        })
      }
      
      if (!rawBarcode) {
        this.notifyScan(source, 'error', 'Por favor ingrese un código de barras válido')
        return 'error'
      }

      this.setScanStatus('loading', 'Analizando código de barras...')

      try {
        console.log(`🔍 Scanner input (${source}):`, rawBarcode)
        
        // Analizar el código de barras para identificar tipo y extraer datos
        const barcodeAnalysis = this.analyzeBarcode(rawBarcode)
        
        console.log('📊 Análisis de código:', barcodeAnalysis)
        console.log('🎯 Datos variables extraídos:', barcodeAnalysis.variableData)

        // Código GS1 con errores de validación (checklist certificación): alertar
        // qué está mal (no numérico, fecha inválida, dígito verificador) y NO procesar
        if (barcodeAnalysis.type === 'GS1' && barcodeAnalysis.validationErrors && barcodeAnalysis.validationErrors.length > 0) {
          this.notifyScan(source, 'error', `⛔ Código GS1 inválido — ${barcodeAnalysis.validationErrors.join(' · ')}`, 8000)
          resetReaderInput()
          return 'error'
        }

        // Código GS1 sin GTIN (solo AIs: lote, vencimiento, SSCC, etc.):
        // mostrar lo parseado bajo el input y quedar listo para el siguiente escaneo
        if (barcodeAnalysis.type === 'GS1' && !barcodeAnalysis.gtin) {
          this.showCodeInformation(barcodeAnalysis, barcodeAnalysis.variableData || {})
          if (!isReader) {
            this.notifyScan(source, 'error', 'El código GS1 no trae GTIN (01): no identifica un producto', 6000)
          }
          resetReaderInput()
          return 'info'
        }

        // Buscar producto usando el GTIN extraído (para GS1) o el código completo (para EAN/UPC)
        const searchBarcode = barcodeAnalysis.gtin || rawBarcode
        
        try {
          const product = await this.$store.dispatch('products/findProductByBarcode', searchBarcode)
          
          // Si llegamos aquí, el producto fue encontrado
          const added = this.addScannedProductWithVariableData(product, barcodeAnalysis, targetIndex)
          if (added === false) {
            // Serie repetida: el aviso ya quedó en la barra de estado del lector;
            // con cámara hay que mostrarlo también en el snackbar
            if (source === 'camera') this.$store.dispatch('showError', this.scanStatus.message)
            resetReaderInput()
            return 'error'
          }

          let statusMessage = `${barcodeAnalysis.type}: ${product.name} agregado`
          if (this.isCaseGtin(product, barcodeAnalysis.gtin)) {
            const v = barcodeAnalysis.variableData || {}
            const cajas = parseInt(v.count || v.quantity, 10) || 1
            statusMessage += ` — ${cajas} caja${cajas === 1 ? '' : 's'} × ${product.units_per_case || 1} un. = ${this.scannedUnits(product, barcodeAnalysis)} un.`
          }
          if (barcodeAnalysis.hasVariableData) {
            const dataInfo = []
            if (barcodeAnalysis.variableData.lot_number) dataInfo.push(`Lote: ${barcodeAnalysis.variableData.lot_number}`)
            if (barcodeAnalysis.variableData.expiry_date) {
              // Determinar qué tipo de fecha se está usando
              if (barcodeAnalysis.variableData.best_before_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.best_before_date) {
                dataInfo.push(`Cons.Pref: ${barcodeAnalysis.variableData.expiry_date}`)
              } else if (barcodeAnalysis.variableData.packaging_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.packaging_date) {
                dataInfo.push(`Empaque: ${barcodeAnalysis.variableData.expiry_date}`)
              } else if (barcodeAnalysis.variableData.production_date && barcodeAnalysis.variableData.expiry_date === barcodeAnalysis.variableData.production_date) {
                dataInfo.push(`Prod: ${barcodeAnalysis.variableData.expiry_date}`)
              } else {
                dataInfo.push(`Venc: ${barcodeAnalysis.variableData.expiry_date}`)
              }
            }
            if (barcodeAnalysis.variableData.serial_number) dataInfo.push(`S/N: ${barcodeAnalysis.variableData.serial_number}`)
            if (dataInfo.length > 0) {
              statusMessage += ` (${dataInfo.join(', ')})`
            }
          }
          
          this.notifyScan(source, 'success', statusMessage)
          resetReaderInput()
          return 'added'
          
        } catch (findError) {
          // Verificar si es específicamente un error de producto no encontrado
          if (findError.message === 'Producto no encontrado') {
            console.log('🔍 Producto no encontrado. Analizando tipo de código:', {
              originalCode: rawBarcode,
              searchedBarcode: searchBarcode,
              gtin: barcodeAnalysis.gtin,
              type: barcodeAnalysis.type,
              source
            })
            
            // Caso ambiguo EAN-8 vs fecha GS1 sola: no hay producto con ese código
            // y es una fecha plausible → mostrar la interpretación GS1, no el modal
            if (barcodeAnalysis.gs1Fallback) {
              console.log('🔀 Sin producto y con fallback GS1 de fecha:', barcodeAnalysis.gs1Fallback)
              this.showCodeInformation({ type: 'GS1', gtin: null }, barcodeAnalysis.gs1Fallback)
              if (!isReader) {
                this.notifyScan(source, 'error', 'El código es una fecha GS1, no identifica un producto', 6000)
              }
              resetReaderInput()
              return 'info'
            }

            // Código con GTIN no registrado (GS1 o no) - mostrar modal de creación
            // (los GS1 sin GTIN ya se informaron antes de la búsqueda)
            // Guardar información del código para el modal
            this.lastScannedCode = rawBarcode
            this.lastExtractedGTIN = searchBarcode
            this.lastBarcodeAnalysis = barcodeAnalysis

            // Limpiar input
            if (isReader) this.multipleScanInput = ''

            // Mostrar modal de confirmación
            this.showCreateProductConfirm = true
            return 'create-product'
          } else {
            // Error real de búsqueda (conexión, etc.)
            throw findError
          }
        }
        
      } catch (error) {
        console.error('Error procesando código de barras:', error)
        this.notifyScan(source, 'error', 'Error al procesar el código de barras')
        return 'error'
      }
    },

    // Unidades que aporta un escaneo: (37) count de unidad logística o (30)
    // cantidad variable; un item con serial siempre es 1 (cada serial es único).
    // Si el GTIN resuelto es el de la CAJA del producto (caja (01) o pallet por
    // su (02)), el conteo viene en cajas y se multiplica por units_per_case.
    scannedUnits(product, barcodeAnalysis) {
      const v = barcodeAnalysis.variableData || {}
      if (v.serial_number) return 1
      const count = parseInt(v.count || v.quantity, 10) || 1
      return this.isCaseGtin(product, barcodeAnalysis.gtin) ? count * (product.units_per_case || 1) : count
    },

    isCaseGtin(product, gtin) {
      if (!product?.case_gtin || !gtin) return false
      const toG14 = c => String(c || '').replace(/\D/g, '').padStart(14, '0')
      return toG14(gtin) === toG14(product.case_gtin)
    },

    addScannedProduct(product, barcode) {
      // Mantener método original para compatibilidad con escaneo simple
      const barcodeAnalysis = { type: 'Simple', gtin: barcode, original: barcode, variableData: null }
      this.addScannedProductWithVariableData(product, barcodeAnalysis)
    },

    addScannedProductWithVariableData(product, barcodeAnalysis, targetIndex = null) {
      // Preparar datos para comparación
      const variableData = barcodeAnalysis.variableData || {}
      
      // Para fechas: vencimiento SOLO desde AI 17/15 (lo que ya resolvió el parser).
      // La fecha de fabricación (AI 11) va aparte, nunca como vencimiento.
      const expiryDate = variableData.expiry_date || ''
      const productionDate = variableData.production_date || ''
      const lotNumber = variableData.lot_number || ''
      const serialNumber = variableData.serial_number || ''
      const scannedQty = this.scannedUnits(product, barcodeAnalysis)

      // Serie repetida = la MISMA unidad física escaneada dos veces: avisar e
      // ignorar ANTES de tocar chips o lista (cada número de serie (21) es
      // una unidad única según GS1 — ni fila nueva ni sumar cantidad)
      if (serialNumber) {
        const dupSerial = this.newIngreso.items.find(item =>
          item.product_id === product.id &&
          (item.serial_number || '') === serialNumber)
        if (dupSerial) {
          this.setScanStatus('error',
            `Serie ${serialNumber} ya escaneada en este ingreso — cada número de serie (21) es una unidad única`, 6000)
          return false
        }
      }

      // Verificar si el producto ya existe con EXACTAMENTE los mismos datos
      const existingProductIndex = this.scannedProducts.findIndex(p => {
        const pVariableData = p.variableData || {}
        const pExpiryDate = pVariableData.expiry_date || ''
        const pLotNumber = pVariableData.lot_number || ''
        const pSerialNumber = pVariableData.serial_number || ''

        return (
          p.id === product.id &&
          pLotNumber === lotNumber &&
          pExpiryDate === expiryDate &&
          pSerialNumber === serialNumber
        )
      })
      
      if (existingProductIndex !== -1) {
        // Si existe con los mismos datos, incrementar la cantidad
        if (window.debugGS1Consolidation) {
          console.log('📋 Incrementando cantidad en scannedProducts:', {
            product: product.name,
            current: this.scannedProducts[existingProductIndex].quantity,
            new: this.scannedProducts[existingProductIndex].quantity + scannedQty
          })
        }
        this.scannedProducts[existingProductIndex].quantity += scannedQty
        this.lastScannedProduct = { ...this.scannedProducts[existingProductIndex], wasIncremented: true, barcodeAnalysis }
      } else {
        // Si no existe, agregarlo como nuevo
        const newProduct = {
          id: product.id,
          name: product.name,
          sku: product.sku,
          barcode: barcodeAnalysis.gtin || barcodeAnalysis.original,
          barcode_original: barcodeAnalysis.original,
          barcode_type: barcodeAnalysis.type,
          quantity: scannedQty,
          variableData: barcodeAnalysis.variableData
        }
        if (window.debugGS1Consolidation) {
          console.log('📋 Agregando nuevo producto a scannedProducts:', {
            product: product.name,
            lot: lotNumber,
            expiry: expiryDate,
            serial: serialNumber
          })
        }
        this.scannedProducts.push(newProduct)
        this.lastScannedProduct = { ...newProduct, wasIncremented: false, barcodeAnalysis }
      }

      // Agregar automáticamente a la lista principal del ingreso con datos variables
      this.addToMainProductListWithVariableData(product, barcodeAnalysis, targetIndex)
      
      // Emitir sonido de éxito (si está disponible)
      this.playSuccessSound()
      return true
    },

    addToMainProductList(product) {
      // Mantener método original para compatibilidad
      const barcodeAnalysis = { variableData: null }
      this.addToMainProductListWithVariableData(product, barcodeAnalysis)
    },

    // targetIndex: card desde el que se abrió la cámara. Si está vacío se llena
    // ese card; si ya tiene otro producto/lote, se reemplaza (el usuario eligió
    // escanear "en" esa fila). Con lector (null) se usa la primera fila vacía.
    addToMainProductListWithVariableData(product, barcodeAnalysis, targetIndex = null) {
      // NUEVA LÓGICA DE CONSOLIDACIÓN INTELIGENTE:
      // - Para códigos idénticos (mismo producto, lote, fecha, serial) → incrementar cantidad
      // - Para códigos diferentes (distinto lote o fecha) → crear nuevo item  
      // - Manejo especial para seriales: no consolidar si ya existe (cada serial es único)
      
      const variableData = barcodeAnalysis.variableData || {}
      
      // Vencimiento SOLO desde AI 17/15. La fabricación (AI 11) va aparte.
      let expiryDate = variableData.expiry_date || ''
      const productionDate = variableData.production_date || ''
      const lotNumber = variableData.lot_number || ''
      const serialNumber = variableData.serial_number || ''
      const scannedQty = this.scannedUnits(product, barcodeAnalysis)

      // Debug detallado solo si está habilitado
      if (window.debugGS1Consolidation) {
        console.log('📦 Buscando producto existente:', {
          product_id: product.id,
          product_name: product.name,
          lot_number: lotNumber,
          expiry_date: expiryDate,
          serial_number: serialNumber,
          current_items: this.newIngreso.items.length
        })
      }
      
      // Serie repetida = la MISMA unidad física escaneada dos veces: no se
      // duplica fila NI se suma cantidad (cada número de serie es una unidad
      // única según GS1) — se avisa y se ignora el escaneo
      if (serialNumber) {
        const dupSerial = this.newIngreso.items.find(item =>
          item.product_id === product.id &&
          (item.serial_number || '') === serialNumber)
        if (dupSerial) {
          this.setScanStatus('error',
            `Serie ${serialNumber} ya escaneada en este ingreso — cada número de serie (21) es una unidad única`, 6000)
          return
        }
        // Serie NUEVA del mismo producto: fila propia con cantidad 1 (el S/N
        // es un campo por fila, no se puede consolidar con otra serie)
      }

      // Buscar item existente que coincida EXACTAMENTE en todos los campos relevantes
      const existingItemIndex = this.newIngreso.items.findIndex(item => {
        // Normalizar fecha de vencimiento para comparación consistente (ambos deben ser '' si están vacíos)
        const itemExpiryDate = item.expiry_date || ''

        return (
          item.product_id === product.id &&
          (item.lot_number || '') === lotNumber &&
          itemExpiryDate === expiryDate &&
          (item.serial_number || '') === serialNumber
        )
      })
      
      if (existingItemIndex !== -1) {
        // Item existente encontrado - incrementar cantidad
        if (window.debugGS1Consolidation) {
          console.log('✅ Producto existente encontrado, incrementando cantidad:', {
            index: existingItemIndex,
            product_name: product.name,
            current_quantity: this.newIngreso.items[existingItemIndex].quantity,
            new_quantity: this.newIngreso.items[existingItemIndex].quantity + scannedQty,
            lot_number: lotNumber,
            expiry_date: expiryDate
          })
        }

        this.newIngreso.items[existingItemIndex].quantity += scannedQty
        return
      }
      
      // No se encontró item existente - crear nuevo
      if (window.debugGS1Consolidation) {
        console.log('📦 Creando nuevo item con datos:', {
          product: product.name,
          lot_number: lotNumber,
          expiry_date: expiryDate,
          serial_number: serialNumber,
          production_date: variableData.production_date,
          items_before: this.newIngreso.items.length
        })
      }
      
      // Fila destino: el card de la cámara si existe; si no, una fila vacía (product_id === null)
      const hasTarget = targetIndex !== null && targetIndex >= 0 && targetIndex < this.newIngreso.items.length
      const emptyItemIndex = hasTarget
        ? targetIndex
        : this.newIngreso.items.findIndex(item => item.product_id === null)
      
      const hasVariableData = barcodeAnalysis.variableData && Object.keys(barcodeAnalysis.variableData).length > 0
      
      const newItem = {
        product_id: product.id,
        quantity: scannedQty,
        lot_number: lotNumber,
        expiry_date: expiryDate,
        serial_number: serialNumber,
        productSearch: '',
        // Metadatos del código de barras (para referencia y re-impresión "tal cual venía")
        barcode_type: barcodeAnalysis.type,
        barcode_original: barcodeAnalysis.original,
        scanned_symbology: barcodeAnalysis.scannedSymbology || null,
        auto_filled: hasVariableData, // Indicar si los datos fueron llenados automáticamente
        production_date: variableData.production_date || '',   // (11)
        packaging_date: variableData.packaging_date || '',     // (13)
        best_before_date: variableData.best_before_date || ''  // (15)
      }
      
      if (emptyItemIndex !== -1) {
        // Si hay una fila vacía, usarla en lugar de crear una nueva
        console.log('📋 Usando fila vacía existente en posición:', emptyItemIndex)
        this.newIngreso.items[emptyItemIndex] = newItem
      } else {
        // Si no hay filas vacías, agregar una nueva
        console.log('📋 Agregando nueva fila')
        this.newIngreso.items.push(newItem)
      }
    },

    undoLastScan() {
      if (!this.lastScannedProduct) {
        this.setScanStatus('error', 'No hay escaneo que deshacer')
        return
      }

      const productToUndo = this.lastScannedProduct
      
      // Encontrar el producto en la lista escaneada
      const scannedIndex = this.scannedProducts.findIndex(p => p.id === productToUndo.id)
      
      if (scannedIndex !== -1) {
        if (productToUndo.wasIncremented && this.scannedProducts[scannedIndex].quantity > 1) {
          // Si fue incrementado, reducir la cantidad
          this.scannedProducts[scannedIndex].quantity -= 1
        } else {
          // Si era nuevo, eliminarlo completamente
          this.scannedProducts.splice(scannedIndex, 1)
        }
      }

      // También deshacer en la lista principal
      const mainIndex = this.newIngreso.items.findIndex(item => item.product_id === productToUndo.id)
      
      if (mainIndex !== -1) {
        if (this.newIngreso.items[mainIndex].quantity > 1) {
          this.newIngreso.items[mainIndex].quantity -= 1
        } else {
          // Solo eliminar si no es el único item y si quantity es 1
          if (this.newIngreso.items.length > 1) {
            this.newIngreso.items.splice(mainIndex, 1)
          } else {
            // Si es el único item, resetear a valores por defecto
            this.newIngreso.items[mainIndex] = blankIngresoItem()
          }
        }
      }

      this.setScanStatus('warning', `Deshecho: ${productToUndo.name}`)
      this.lastScannedProduct = null
    },

    clearScannedProducts() {
      const confirmClear = confirm('¿Está seguro que desea limpiar todos los productos escaneados?')
      
      if (confirmClear) {
        // Limpiar productos escaneados
        this.scannedProducts = []
        this.lastScannedProduct = null
        
        // Resetear la lista principal a un item vacío
        this.newIngreso.items = [
          blankIngresoItem()
        ]
        
        this.setScanStatus('info', 'Todos los productos han sido eliminados')
        
        // Volver a enfocar el input si está en modo escaneo múltiple
        if (this.isMultipleScanMode) {
          this.$nextTick(() => {
            if (this.$refs.multipleScanInput) {
              this.$refs.multipleScanInput.focus()
            }
          })
        }
      }
    },

    setScanStatus(type, message, duration = 3000) {
      this.scanStatus = { type, message }

      // Cancelar el timer anterior para que no borre este mensaje antes de tiempo
      if (this.scanStatusTimer) {
        clearTimeout(this.scanStatusTimer)
        this.scanStatusTimer = null
      }

      // Auto-limpiar mensajes después de unos segundos
      if (type !== 'loading') {
        this.scanStatusTimer = setTimeout(() => {
          this.scanStatus = { type: '', message: '' }
        }, duration)
      }
    },

    playSuccessSound() {
      // Intentar reproducir un sonido de éxito
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhCKDW8tl+KwUu')
        audio.volume = 0.3
        audio.play().catch(() => {
          // Silenciar errores de audio si no se puede reproducir
        })
      } catch (error) {
        // Silenciar errores de audio
      }
    },

    // ========== MÉTODOS DE IMPRESIÓN ==========

    // Obtener código de barras de un producto por ID
    getProductBarcode(productId) {
      if (!productId || !this.products) return null
      const product = this.products.find(p => p.id === productId)
      return product?.barcode || null
    },

    // Verificar si un código de barras es EAN-13
    isEAN13(barcode) {
      if (!barcode) return false
      // EAN-13 debe tener exactamente 13 dígitos y empezar por 789 (nuestro patrón)
      return /^789\d{10}$/.test(barcode.toString())
    },

    // Un GTIN numérico de hasta 14 dígitos se puede imprimir en alguna simbología GS1
    isPrintableBarcode(barcode) {
      return symbologyAvailable('itf14', barcode)
    },

    // El detalle guardado puede traer product_id plano o el producto anidado
    detailProductId(item) {
      return item.product_id || (item.product && item.product.id) || null
    },

    // Reconstruye el element string GS1 desde el escaneo crudo, conservando
    // TODOS los AIs en el ORDEN original del código leído (re-impresión fiel).
    elementStringFromScan(raw) {
      if (!raw) return null
      try {
        const cleaned = String(raw).trim()
          .replace(/^[\]+]([A-Za-z][0-9A-Za-z])/, '')
          .replace(/[()]/g, '')
        const { items } = this.parseGS1(cleaned)
        if (!items.length) return null
        return items.map(it => `(${it.ai})${it.value}`).join('')
      } catch (e) {
        return null
      }
    },

    // Mostrar modal de impresión para un producto.
    // `item` (línea de ingreso o detalle guardado) aporta lote/fechas/serie para
    // reconstruir el código GS1 completo; si la línea conserva el escaneo original
    // (barcode_original), se re-imprime "tal cual venía" (mismos AIs, mismo orden).
    showPrintProductDialog(productId, quantity = 1, item = null) {
      if (!productId || !this.products) return

      const product = this.products.find(p => p.id === productId)
      if (!product || !product.barcode) {
        this.$store.dispatch('showError', 'Producto no tiene código de barras válido')
        return
      }

      if (!this.isPrintableBarcode(product.barcode)) {
        this.$store.dispatch('showError', 'El código debe ser numérico de hasta 14 dígitos (GTIN) para imprimir simbologías GS1')
        return
      }

      // Element string fiel al escaneo (solo si la línea viene de un escaneo GS1)
      const scannedElementString = (item?.barcode_type === 'GS1' && item?.barcode_original)
        ? this.elementStringFromScan(item.barcode_original)
        : null
      // (01) del escaneo original: decide si el element string aplica al GTIN
      // que se imprime y si la línea entró por etiqueta de CAJA
      const scannedGtin = (scannedElementString || '').match(/\(01\)(\d{14})/)?.[1] || null

      // Cantidad + datos GS1 de la línea (lote/fechas/serie) para el element string
      this.selectedProductForPrint = {
        ...product,
        quantity: quantity,
        lot_number: item?.lot_number || null,
        expiry_date: item?.expiry_date || null,
        production_date: item?.production_date || null,
        serial_number: item?.serial_number || null,
        gs1_element_string: scannedElementString,
        gs1_element_string_gtin: scannedGtin,
        scanned_symbology: item?.scanned_symbology || null
      }

      // Destino por defecto: la caja si la línea se escaneó por su GTIN de caja;
      // si no, la unidad. Siempre parte en UNA etiqueta.
      const scannedIsCase = !!(scannedGtin && product.case_gtin && this.isCaseGtin(product, scannedGtin))
      this.printSettings.target = (scannedIsCase && this.canPrintCase) ? 'case' : 'unit'
      this.printSettings.printAll = false
      this.printSettings.aiOrder = []
      this.applyDefaultSymbology()
      this.showPrintDialog = true

      // Generar vista previa después de que el modal se abra
      this.$nextTick(() => {
        this.generatePrintPreview()
      })
    },

    // Simbología por defecto para el GTIN destino: (1) la detectada por AIM al
    // escanear; (2) GS1-128 si la línea trae AIs (para que la vista previa
    // muestre el código COMPLETO); (3) EAN-13 / ITF-14 para códigos sin datos
    // variables (un GTIN-14 de caja no cabe en EAN-13 → ITF-14).
    applyDefaultSymbology() {
      const p = this.selectedProductForPrint
      const gtin = this.printTargetGtin
      if (!p || !gtin) return
      const hasGS1Data = !!(p.gs1_element_string || p.lot_number || p.expiry_date || p.production_date || p.serial_number)
      if (p.scanned_symbology && symbologyAvailable(p.scanned_symbology, gtin)) {
        this.printSettings.symbology = p.scanned_symbology
      } else if (hasGS1Data) {
        this.printSettings.symbology = 'gs1-128'
      } else if (!symbologyAvailable(this.printSettings.symbology, gtin)) {
        this.printSettings.symbology = symbologyAvailable('ean13', gtin) ? 'ean13' : 'itf14'
      }
    },

    // Impresión DIRECTA en la Zebra (ZPL nativo vía Browser Print): el símbolo
    // lo dibuja la impresora por dot — calidad de verificación (a diferencia
    // del botón Imprimir, que rasteriza por el navegador). ^PQ imprime la
    // cantidad del producto automáticamente.
    async printBarcodeZebra() {
      const p = this.selectedProductForPrint
      if (!p) return
      this.zebraPrinting = true
      try {
        const data = this.printBarcodeData
        const elementString = AI_SYMBOLOGIES.includes(this.printSettings.symbology)
          ? buildGS1ElementString(data)
          : null
        const gtin = this.printTargetGtin
        const copies = this.printCopies
        // Resolver ANTES la impresora (elegida en este equipo o la por defecto)
        // para generar el ZPL con su perfil (la 2844-Z no admite UTF-8)
        const target = await resolveBrowserPrintTarget()
        const zpl = buildSymbologyPrintZPL(this.printSettings.symbology, {
          productName: p.name,
          gtin,
          elementString,
          // Para un UPC-E real la expansión va SIEMPRE (todas las simbologías
          // GTIN la usan como base), no solo al imprimir como UPC-E
          upcaExpansion: (() => {
            const d = String(gtin || '').replace(/\D/g, '')
            return d.length === 8 && !isValidGS1Key(d, [8]) ? expandUPCE(d) : null
          })(),
          quantity: copies,
          // DataBar: símbolo exacto de bwip-js dibujado con rectángulos ^GB
          // (cualquier orden de AIs, filas balanceadas, idéntico a la vista
          // previa, compatible con todo firmware) en vez del ^BR nativo
          databarBars: this.printSettings.symbology === 'databarexpandedstacked' && elementString
            ? (x0, y0) => databarZplBars(elementString, { x0, y0 })
            : null
        }, target.profile)
        if (!zpl) {
          this.$store.dispatch('showError', this.printSettings.symbology === 'databarexpandedstacked'
            ? 'DataBar en ZPL: los AI de largo variable (lote, serie…) deben ir AL FINAL — vuelve al orden natural o usa el botón Imprimir (navegador) para este orden'
            : 'Esta simbología no tiene impresión ZPL nativa — usa el botón Imprimir (navegador)')
          return
        }
        const printerName = await printZplViaBrowserPrint(zpl, target)
        this.$store.dispatch('showSuccess', `${copies} etiqueta(s) enviadas a "${printerName}" vía Browser Print`)
        this.showPrintDialog = false
      } catch (e) {
        console.error('Error imprimiendo en Zebra:', e)
        if (e.code === 'no-service') {
          this.$store.dispatch('showError', 'Zebra Browser Print no responde: verifica que la app esté corriendo y autoriza el sitio. Alternativa: botón Imprimir (navegador)')
        } else if (e.code === 'printer-missing') {
          this.$store.dispatch('showError', `${e.message}: conéctala o elige otra en el selector de impresora`)
        } else {
          this.$store.dispatch('showError', `Browser Print no pudo imprimir (${e.message}). Revisa la consola (F12) — usa el botón Imprimir mientras tanto`)
        }
      } finally {
        this.zebraPrinting = false
      }
    },

    // Reordenar los AIs del element string a imprimir (chips del diálogo)
    moveAi(index, dir) {
      const order = [...this.printAiList]
      const j = index + dir
      if (j < 0 || j >= order.length) return
      const tmp = order[index]
      order[index] = order[j]
      order[j] = tmp
      this.printSettings.aiOrder = order
      this.$nextTick(() => this.generatePrintPreview())
    },
    resetAiOrder() {
      this.printSettings.aiOrder = []
      this.$nextTick(() => this.generatePrintPreview())
    },

    // Generar vista previa de códigos de barras (bwip-js, simbología seleccionable)
    generatePrintPreview() {
      if (!this.printTargetGtin) return

      const copies = this.printCopies
      const data = this.printBarcodeData
      const symbology = this.printSettings.symbology

      // Usar setTimeout para asegurar que los canvas estén en el DOM
      setTimeout(() => {
        for (let i = 1; i <= copies; i++) {
          const canvasArray = this.$refs[`previewCanvas${i}`]
          const canvas = Array.isArray(canvasArray) ? canvasArray[0] : canvasArray

          if (canvas) {
            try {
              renderBarcodeToCanvas(canvas, symbology, data, this.printSettings.labelSize)
            } catch (error) {
              console.error('Error generando preview de código de barras:', error)
              this.$store.dispatch('showError', `No se pudo generar ${symbology}: ${error.message || error}`)
              return
            }
          }
        }
      }, 100)
    },

    // Generar imagen de código de barras como base64 (simbología seleccionada)
    async generateBarcodeImage(data, labelSize = 'medium') {
      try {
        return renderBarcodeToDataURL(this.printSettings.symbology, data, labelSize)
      } catch (error) {
        console.error('Error generating barcode image:', error)
        this.$store.dispatch('showError', `No se pudo generar el código: ${error.message || error}`)
        return ''
      }
    },

    // Imprimir códigos de barras del producto
    async printProductBarcode() {
      if (!this.selectedProductForPrint || !this.printTargetGtin) return

      try {
        this.printing = true

        const product = this.selectedProductForPrint
        const copies = this.printCopies

        // Crear lista de "productos" para usar las funciones existentes de impresión
        const productsToPrint = []
        for (let i = 0; i < copies; i++) {
          productsToPrint.push({
            id: product.id,
            name: product.name,
            sku: product.sku,
            barcode: this.printTargetGtin
          })
        }
        
        // Generar imágenes de códigos de barras (con lote/vencimiento si es GS1)
        const productsWithBarcodes = await Promise.all(
          productsToPrint.map(async (prod) => {
            const barcodeImage = await this.generateBarcodeImage(this.printBarcodeData, this.printSettings.labelSize)

            return {
              ...prod,
              barcodeImage: barcodeImage
            }
          })
        )
        
        let printContent = ''
        
        if (this.printSettings.printerType === 'standard') {
          printContent = this.generateStandardPrintContent(productsWithBarcodes)
        } else {
          printContent = this.generateLabelPrintContent(productsWithBarcodes)
        }
        
        // Abrir ventana de impresión
        const printWindow = window.open('', '_blank')
        printWindow.document.write(printContent)
        printWindow.document.close()
        printWindow.focus()
        
        setTimeout(() => {
          printWindow.print()
          printWindow.close()
        }, 500)
        
        this.showPrintDialog = false
        this.$store.dispatch('showSuccess', `${copies} etiqueta(s) enviada(s) a impresión`)
        
      } catch (error) {
        console.error('Error imprimiendo códigos de barras:', error)
        this.$store.dispatch('showError', 'Error al imprimir códigos de barras')
      } finally {
        this.printing = false
      }
    },

    // Generar contenido HTML para impresión estándar
    generateStandardPrintContent(productsList) {
      let content = `
        <html>
          <head>
            <title>Códigos de Barras - ${this.selectedProductForPrint.name}</title>
            <style>
              @page {
                margin: 1cm;
                size: A4 portrait;
              }
              
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 0; 
                background: white;
                max-width: 19cm; /* A4 - márgenes */
              }
              
              .print-grid { 
                display: grid; 
                grid-template-columns: repeat(${this.printSettings.labelsPerRow}, minmax(0, 1fr)); 
                gap: 8px; 
                width: 100%;
                max-width: 100%;
                justify-content: center;
                padding: 10px;
              }
              
              .barcode-label { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: center; 
                page-break-inside: avoid;
                box-sizing: border-box;
                overflow: hidden;
              }
              
              /* Tamaños responsivos que se adaptan al grid */
              .barcode-label.small { 
                min-width: 1.5cm; 
                max-width: 3cm;
                height: 1.2cm; 
                font-size: 7px; 
              }
              .barcode-label.medium { 
                min-width: 3cm; 
                max-width: 5cm;
                height: 2.5cm; 
                font-size: 9px; 
              }
              .barcode-label.large { 
                min-width: 4cm; 
                max-width: 7cm;
                height: 3.5cm; 
                font-size: 11px; 
              }
              

              
              .barcode-image { 
                max-width: 95%; 
                height: auto; 
                margin: 2px 0;
                display: block;
                margin-left: auto;
                margin-right: auto;
              }
              
              .barcode-text { 
                font-family: monospace; 
                font-size: 0.8em; 
                color: #333; 
                margin-top: 2px;
                word-break: break-all;
              }
              
              /* Estilos específicos para impresión */
              @media print {
                body { 
                  margin: 0 !important;
                  padding: 0 !important;
                }
                .print-grid {
                  padding: 5px !important;
                }
                .barcode-label {
                  break-inside: avoid;
                  border: 1px solid #000 !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-grid">
      `
      
      productsList.forEach((product) => {
        content += `
          <div class="barcode-label ${this.printSettings.labelSize}">
            ${product.barcodeImage ? `<img src="${product.barcodeImage}" class="barcode-image" alt="${product.barcode}">` : ''}
            <div class="barcode-text">${product.barcode}</div>
          </div>
        `
      })
      
      content += `
            </div>
          </body>
        </html>
      `
      
      return content
    },

    // Generar contenido HTML para impresión de etiquetas
    generateLabelPrintContent(productsList) {
      let content = `
        <html>
          <head>
            <title>Etiquetas - ${this.selectedProductForPrint.name}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
              .label { 
                width: ${this.printSettings.labelFormat === '2x1' ? '2in' : 
                         this.printSettings.labelFormat === '4x2' ? '4in' : '4in'}; 
                height: ${this.printSettings.labelFormat === '2x1' ? '1in' : 
                          this.printSettings.labelFormat === '4x2' ? '2in' : '6in'}; 
                border: 1px solid #000; 
                margin: 5px; 
                padding: 10px; 
                text-align: center;
                display: inline-block;
                page-break-inside: avoid;
              }

              .barcode-image { max-width: 90%; height: auto; margin: 5px 0; }
              .barcode-text { font-family: monospace; font-size: 10px; color: #666; }
            </style>
          </head>
          <body>
      `
      
      productsList.forEach((product) => {
        content += `
          <div class="label">
            ${product.barcodeImage ? `<img src="${product.barcodeImage}" class="barcode-image" alt="${product.barcode}">` : ''}
            <div class="barcode-text">${product.barcode}</div>
          </div>
        `
      })
      
      content += `
          </body>
        </html>
      `
      
      return content
    },

    // ========== FUNCIONES INFORMATIVAS PARA CÓDIGOS NO COMERCIALES ==========
    
    showCodeInformation(analysis, productData) {
      const codeType = this.identifyCodeType(analysis, productData)
      const details = this.formatCodeDetails(productData).replace(/\n/g, ' · ')

      // Mostrar lo parseado bajo el input de escaneo por 5 segundos
      this.setScanStatus('info', `Código ${codeType}: ${details}`, 5000)

      console.log(`📊 [INFO] Código no comercial parseado:`, {
        type: codeType,
        gtin: analysis.gtin,
        data: productData
      })
    },
    
    showBasicCodeInformation(analysis) {
      const codeType = this.identifyCodeTypeByGTIN(analysis.gtin)
      const message = `📦 Código ${codeType} detectado:\n🏷️ GTIN: ${analysis.gtin}\n\n⚡ Listo para siguiente escaneo`
      
      // Mostrar mensaje informativo usando Vuex
      this.$store.dispatch('showInfo', message)
      
      // También usar setScanStatus para consistencia
      this.setScanStatus('info', `Código ${codeType} parseado correctamente`)
      
      console.log(`📦 [INFO] Código GS1 básico:`, {
        type: codeType,
        gtin: analysis.gtin
      })
    },
    
    identifyCodeType(analysis, productData) {
      // Identificar tipo de código basado en los AIs presentes
      if (productData.sscc) return 'CONTENEDOR (SSCC)'
      if (productData.gsin) return 'ENVÍO (GSIN)'  
      if (productData.gdti) return 'DOCUMENTO (GDTI)'
      if (productData.gln) return 'UBICACIÓN (GLN)'
      if (productData.gross_weight_kg || productData.net_weight_kg) return 'PRODUCTO CON PESO'
      if (productData.custom_field_90 || productData.custom_field_91) return 'CÓDIGO PERSONALIZADO'
      if (analysis.gtin) return 'PRODUCTO GS1'
      
      return 'GS1'
    },
    
    identifyCodeTypeByGTIN(gtin) {
      if (!gtin) return 'GS1'
      
      // Análisis básico del GTIN para identificar tipo
      if (gtin.startsWith('00')) return 'CONTENEDOR/PALLET'
      if (gtin.startsWith('02')) return 'PRODUCTO VARIABLE'
      if (gtin.startsWith('1') || gtin.startsWith('2')) return 'PRODUCTO COMERCIAL'
      
      return 'PRODUCTO GS1'
    },
    
    formatCodeDetails(productData) {
      const details = []
      
      // Información principal
      if (productData.sscc) details.push(`📦 SSCC: ${productData.sscc}`)
      if (productData.gsin) details.push(`🚛 GSIN: ${productData.gsin}`)
      if (productData.gdti) details.push(`📄 GDTI: ${productData.gdti}`)
      if (productData.gln) details.push(`🏢 GLN: ${productData.gln}`)
      if (productData.ginc) details.push(`🚚 GINC: ${productData.ginc}`)
      if (productData.grai) details.push(`♻️ GRAI: ${productData.grai}`)
      if (productData.giai) details.push(`🔩 GIAI: ${productData.giai}`)
      if (productData.gsrn) details.push(`🤝 GSRN: ${productData.gsrn}`)

      // Información de producto
      if (productData.lot_number) details.push(`🏷️ Lote: ${productData.lot_number}`)
      if (productData.serial_number) details.push(`🔢 Serie: ${productData.serial_number}`)
      if (productData.expiry_date) {
        // Si el "vencimiento" proviene del AI 15 (fallback), etiquetarlo como consumo preferente
        if (productData.best_before_date && productData.best_before_date === productData.expiry_date) {
          details.push(`📅 Cons.Pref.: ${this.formatDate(productData.expiry_date)}`)
        } else {
          details.push(`📅 Vence: ${this.formatDate(productData.expiry_date)}`)
        }
      }
      if (productData.production_date) details.push(`🏭 Prod.: ${this.formatDate(productData.production_date)}`)
      if (productData.packaging_date) details.push(`📦 Empaque: ${this.formatDate(productData.packaging_date)}`)
      if (productData.quantity) details.push(`🔢 Cantidad: ${productData.quantity}`)
      if (productData.count) details.push(`#️⃣ Unidades: ${productData.count}`)
      
      // Pesos
      if (productData.gross_weight_kg) details.push(`⚖️ Peso bruto: ${productData.gross_weight_kg}kg`)
      if (productData.net_weight_kg) details.push(`⚖️ Peso neto: ${productData.net_weight_kg}kg`)
      if (productData.gross_weight_lb) details.push(`⚖️ Peso bruto: ${productData.gross_weight_lb}lb`)
      if (productData.net_weight_lb) details.push(`⚖️ Peso neto: ${productData.net_weight_lb}lb`)
      
      // Campos personalizados
      const customFields = ['custom_field_90', 'custom_field_91', 'custom_field_92', 
                           'custom_field_93', 'custom_field_94', 'custom_field_95',
                           'custom_field_96', 'custom_field_97', 'custom_field_98', 'custom_field_99']
      
      customFields.forEach((field, index) => {
        if (productData[field]) {
          details.push(`🔧 Campo ${90 + index}: ${productData[field]}`)
        }
      })

      // AIs válidos sin campo propio (capturados por el parser genérico)
      if (Array.isArray(productData.other_ais)) {
        productData.other_ais.forEach(entry => details.push(`🔖 ${entry}`))
      }

      return details.length > 0 ? details.join('\n') : '📋 Información GS1 detectada'
    }
  },
  watch: {
    // Regenerar vista previa cuando cambian los ajustes de impresión
    'printSettings.printerType'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    'printSettings.labelSize'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    'printSettings.symbology'() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    // Cambiar unidad ↔ caja cambia el GTIN: la simbología por defecto se
    // reevalúa (EAN-13 no admite un GTIN-14 de caja) y se redibuja la vista previa
    'printSettings.target'() {
      if (this.showPrintDialog) {
        this.applyDefaultSymbology()
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    printCopies() {
      if (this.showPrintDialog) {
        this.$nextTick(() => this.generatePrintPreview())
      }
    },
    // Los switches de modo se recuerdan por usuario en el backend: la
    // próxima vez que abra Ingresos aparecen como los dejó la última vez
    simplifiedMode(value) {
      this.saveUserPreference('ingreso_simplified_mode', value)
    },
    'storageConfig.pieceByPieceEnabled'(value) {
      this.saveUserPreference('storage_piece_by_piece', value)
    }
  },
  mounted() {
    this.fetchIngresos()
    this.fetchProducts()
    this.fetchLocations()
    this.fetchZones()
    this.fetchWarehouses()
    this.loadUserPreferences()

          // Ejecutar pruebas GS1 en desarrollo (solo si está habilitado por consola)
      // Para habilitar: ejecutar window.enableGS1Tests = true en la consola
      // Para pruebas de lotes: window.enableLotTests = true
      if (process.env.NODE_ENV === 'development' && window.enableGS1Tests) {
        setTimeout(() => {
          this.testGS1Parsing()
        }, 1000)
      }
      if (process.env.NODE_ENV === 'development' && window.enableLotTests) {
        setTimeout(() => {
          this.testLotParsing()
        }, 1500)
      }
  },
  beforeUnmount() {
    this.stopScanner()
  }
}
</script>

<style scoped>
.ingresos-container {
  padding: 20px;
}

/* Card por producto en el modal de nuevo ingreso */
.ingreso-item-card {
  padding: 14px 16px 10px;
  border-color: #dfe3e8 !important;
  border-radius: 10px !important;
  background: #fafbfc;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.ingreso-item-card:hover,
.ingreso-item-card:focus-within {
  border-color: #90a4ae !important;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
/* Línea llenada desde un código GS1: acento verde */
.ingreso-item-card--gs1 {
  border-left: 4px solid #43a047 !important;
}
.ingreso-item-card__header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.ingreso-item-card__index {
  flex: 0 0 28px;
  height: 28px;
  margin-top: 12px;
  border-radius: 50%;
  background: #eceff1;
  color: #546e7a;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ingreso-item-card__product {
  flex: 1 1 auto;
  min-width: 0;
}
/* Los hints (SKU/GTIN, "Desde el código GS1") no se cortan */
.ingreso-item-card :deep(.v-messages__message) {
  white-space: normal;
  line-height: 1.3;
}
.ingreso-item-card__actions {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
}
.ingreso-item-card__fields {
  margin-top: 6px;
}
.ingreso-item-card__extra {
  margin-top: 2px;
}
.ingreso-item-card__raw {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.ingreso-item-card__raw code {
  font-size: 12px;
  background: #eceff1;
  color: #37474f;
  padding: 2px 6px;
  border-radius: 4px;
  word-break: break-all;
}
@media (max-width: 600px) {
  .ingreso-item-card__header {
    flex-wrap: wrap;
  }
  .ingreso-item-card__actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: 0;
  }
}

/* Estilos para la tabla de ingresos */
.ingresos-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white !important;
}

.ingresos-table .v-data-table__thead {
  background-color: #f8f9fa !important;
}

.ingresos-table .v-data-table__thead .v-data-table__th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 12px 16px !important;
  font-size: 0.875rem !important;
}

.ingresos-table .v-data-table__tbody .v-data-table__td {
  padding: 12px 16px !important;
  border-bottom: 1px solid #dee2e6 !important;
}

.ingresos-table .v-data-table__tbody tr:hover {
  background-color: #f8f9fa !important;
}

.detail-group {
  margin-bottom: 1rem;
}

.detail-group label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 0.25rem;
  display: block;
}

.detail-group p {
  margin: 0;
}

.v-data-table {
  background: transparent !important;
}

.v-card {
  border-radius: 8px;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-card-text {
  padding-top: 20px;
}

.v-divider {
  margin: 16px 0;
}

.text-h6 {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.v-btn {
  text-transform: none;
}

.v-alert {
  margin-top: 16px;
}

.v-chip {
  font-weight: 500;
}

.v-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.v-table th {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  background-color: #f5f5f5;
}

.v-table td {
  color: rgba(0, 0, 0, 0.87);
}

.v-table tr:hover {
  background-color: #f5f5f5;
}

.barcode-scanner {
  width: 100%;
  max-width: 400px;
  height: 300px;
  object-fit: cover;
  border-radius: 8px;
}

/* Estilos para el modal de configuración de almacenamiento */
.switch-container {
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.switch-container .v-switch {
  margin-bottom: 0;
}

.switch-container .v-switch .v-selection-control__wrapper {
  height: auto;
}

/* Mejorar la apariencia del switch */
.v-switch .v-selection-control__input {
  border-radius: 12px;
}

.v-switch .v-selection-control__input:before {
  border-radius: 12px;
}

/* Asegurar que el switch se vea habilitado cuando está activo */
.v-switch.v-input--is-dirty .v-selection-control__input {
  color: rgb(var(--v-theme-primary)) !important;
}

.v-switch.v-input--is-dirty .v-selection-control__input:before {
  background-color: rgb(var(--v-theme-primary)) !important;
  opacity: 0.2;
}

.v-switch.v-input--is-dirty .v-selection-control__thumb {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: white !important;
}

/* Estilos para las alertas del modal */
.v-alert ul {
  margin-left: 16px;
  margin-bottom: 0;
}

.v-alert li {
  margin-bottom: 4px;
}

/* Estilos para impresión de códigos de barras */
.print-preview-container {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  background: #f9f9f9;
  min-height: 120px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}

.print-preview-container.standard {
  max-height: 300px;
  overflow-y: auto;
}

.preview-label {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  text-align: center;
  background: white;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.preview-label.small { 
  width: 80px; 
  height: 40px; 
  font-size: 8px; 
}

.preview-label.medium { 
  width: 120px; 
  height: 60px; 
  font-size: 10px; 
}

.preview-label.large { 
  width: 160px; 
  height: 80px; 
  font-size: 12px; 
}



.preview-barcode-canvas {
  max-width: 100%;
  height: auto;
}

.barcode-text {
  font-family: 'Courier New', monospace;
  font-size: 0.75em;
  color: #666;
  margin-top: 2px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .ingresos-container {
    padding: 10px;
  }
  
  .ingresos-title {
    font-size: 1.1rem !important;
    margin-bottom: 8px;
  }
  
  /* Filtros responsivos */
  .ingresos-filters {
    margin-bottom: 16px !important;
  }
  
  .ingresos-filters .v-col {
    padding: 4px 8px !important;
  }
  
  .v-text-field, .v-select {
    margin-bottom: 8px;
  }
  
  .v-text-field .v-field__input,
  .v-select .v-field__input {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }
  
  /* Cards para resumen */
  .v-card-title {
    font-size: 1rem !important;
    padding: 12px 16px 8px !important;
  }
  
  .v-card-text {
    padding: 8px 16px 16px !important;
  }
  
  /* Optimización de espacios */
  .v-row {
    margin: 0 -8px !important;
  }
  
  .v-col {
    padding: 0 8px !important;
  }
  
  /* Modal responsivo */
  .v-dialog .v-card {
    margin: 16px !important;
    width: calc(100% - 32px) !important;
    max-width: none !important;
  }
  
  .v-dialog .v-card-title {
    font-size: 1.1rem !important;
    padding: 16px !important;
  }
  
  .v-dialog .v-card-text {
    padding: 0 16px 16px !important;
  }
}

@media (max-width: 480px) {
  .ingresos-container {
    padding: 8px;
  }
  
  .ingresos-title {
    font-size: 1rem !important;
  }
  
  /* Botones más pequeños en móvil */
  .v-btn {
    min-width: 40px !important;
    height: 36px !important;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 0.75rem !important;
  }
}
</style>
